<template>
	<div class="report-builder">
		<div class="report-actions">
			<div class="report-name-section">
				<FormInput
					type="text"
					v-model="reportName"
					name="reportName"
					:label="'Default project: ' + projectName"
					:required="false"
					placeholder="Enter report name..."
					:noMarginBottom="true"
					class="report-name-input"
				/>
				<Icon v-if="reportName.trim()" type="settings" class="icon" @clicked="showReportSettingsModal = true" titleFancy="Settings" titleFancyPosition="left" />
				<Icon v-if="reportName.trim()" type="csv" class="icon" @clicked="showCSVcustomModal = true" titleFancy="Custom Info CSV" />
			</div>
			<div class="action-buttons" v-if="reportName && reportItems.length > 0">
				<span :class="['large-button', 'primary', { disabled: generatingReport }]" @click="generateReport(true)">
					{{ generatingReport ? "Generating..." : "Preview" }}
				</span>
				<span :class="['large-button', 'primary', { disabled: generatingReport }]" @click="generateReport()">
					{{ generatingReport ? "Generating..." : "Download" }}
				</span>
				<span :class="['large-button', { disabled: savingReport }]" @click="saveReport(['settings','name'])" v-html="savingReport ? 'Saving...' : 'Save'" />
			</div>
		</div>

		<div class="report-content-wrapper">
			<div class="report-components">
				<h3 class="available-components-title">Available Components</h3>
				<draggable
					:list="availableComponentsList"
					:group="{ name: 'components', pull: 'clone', put: false }"
					:sort="false"
					:clone="cloneComponent"
					@start="drag = true"
					@end="drag = false"
				>
					<div v-for="element in availableComponentsList" :key="element.id" class="component-item" @click="cloneComponent(element, true)">
						<Icon :type="element.icon" class="component-icon" v-if="element.icon" />
						<span class="component-name">{{ element.name }}</span>
					</div>
				</draggable>
			</div>

			<div class="report-canvas">
				<h3 class="report-title">Report Layout</h3>
				<!-- Add these buttons around the report layout list -->
				<div class="report-layout">
					<span class="large-button mini" @click="handleHeaderFooterClick('header')">Edit Header</span>

					<p v-if="reportItemsEmpty" class="no-components-message">
						Click a component to the left to get started.
					</p>

					<!-- Existing draggable report layout list -->
					<draggable
						v-model="reportItems"
						:class="{'dragArea': true, 'list-group': true }"
						group="components"
						:item-key="item => item.id"
						@change="handleChange"
						@start="drag = true"
						@end="drag = false"
						v-if="!reportItemsEmpty"
					>
						<div
							v-for="(reportItem, index) in reportItems"
							:key="'drag-'+reportItem.id"
							:class="{'list-group-item has-remove': true, [reportItem.type]: true, 'error': !!getFriendlyError(index), 'indented': reportItemsHasPageBreakAt>0&&reportItemsHasPageBreakAt<=index&&reportItem.type !== 'newPage'}"
							@click="handleChange({ added: { element: reportItem } })"
						>
							<Icon :type="reportItemIcon(reportItem)" class="component-icon" v-if="reportItemIcon(reportItem)" />
							<span class="component-name">
								<span>{{ reportItem.name }}</span>
								<span v-if="getFriendlyError(index)" class="error-message">: {{ getFriendlyError(index) }}</span>
							</span>
							<button @click="removeItem($event, reportItem)" class="remove-btn">×</button>
						</div>
					</draggable>
					
					<span class="large-button mini" @click="handleHeaderFooterClick('footer')">Edit Footer</span>
				</div>
			</div>
		</div>

		<!-- Main Components Settings Modal -->
		<div v-if="showSettingsModal" class="modal settings-modal has-modal-actions">
			<div class="modal-wrap">
				<div class="modal-inner">
					<h2 class="modal-title">{{ `${activeComponentTypeTitle} Settings` }} <span class="close" @click="saveSettings(null,true)">X</span></h2>

					<div class="modal-inner-scroll">
						<form class="form" @submit.prevent="saveSettings">
							<div class="option-wrap">
								<FormInput
									type="text"
									v-model="componentSettings.name"
									name="name"
									label="Component Name (just for your reference)"
									:required="false"
									:noMarginBottom="true" 
									 v-if="!(activeComponent.type === 'header' || activeComponent.type === 'footer')"
								/>

								<!-- Header Settings -->
								<template v-if="activeComponent && (activeComponent.type === 'header' || activeComponent.type === 'footer')">
									<div class="setting-group">
										<ImagePreview 
											:value="componentSettings.logo"
											@show-uploader="showLogoUploader = true"
											@show-size-info="showImageSizeInfo"
											@delete-image="deleteImage"
										>
											<span v-if="componentSettings.logo">
												Image width/height/distance is based on a % of the page width. <br>Height overrides width, distance from right overrides left.
											</span>
										</ImagePreview>
									</div>
									<!-- Update class name and structure -->
									<div class="setting-group logo-dimensions" v-if="componentSettings.logo">
										<FormInput
											type="number"
											v-model.number="componentSettings.logoWidth"
											name="logoWidth"
											label="Width (%)"
											:min="1"
											:max="100"
											:required="false"
											:noMarginBottom="true"
											v-if="!componentSettings.logoHeight" 
											class="logo-dimensions-input"
										/>
										<FormInput
											type="number"
											v-model.number="componentSettings.logoHeight"
											name="logoHeight"
											label="Height (%)"
											:min="1"
											:max="100"
											placeholder="Defaults to 3%"
											:required="false"
											:noMarginBottom="true"
											v-if="!componentSettings.logoWidth"
											class="logo-dimensions-input"
										/>
										<FormInput
											type="number"
											v-model.number="componentSettings.logoLeft"
											name="logoLeft"
											label="Distance From Left (%)"
											:min="0"
											:max="100"
											:required="false"
											:noMarginBottom="true"
											v-if="!componentSettings.logoRight"
											class="logo-dimensions-input"
										/>
										<FormInput
											type="number"
											v-model.number="componentSettings.logoRight"
											name="logoRight"
											label="Distance From Right (%)"
											:min="0"
											:max="100"
											placeholder="Defaults to 5%"
											:required="false"
											:noMarginBottom="true"
											v-if="!componentSettings.logoLeft"
											class="logo-dimensions-input"
										/>
									</div>

									<hr />

									<div class="setting-group">
										<Switcher
											v-model="componentSettings.displayPagination"
											:switcherLabel="activeComponent.type === 'header' ? 'Display pagination?' : 'Display page count?'"
											:sameColour="true"
											:title="
												activeComponent.type === 'header'
													? 'Toggle to display pagination on every page'
													: 'Toggle to display page count on every page'
											"
										/>
									</div>

									<div class="setting-group" v-if="!componentSettings.displayPagination">
										<FormInput
											type="text"
											v-model="componentSettings.headerText"
											name="headerText"
											:label="activeComponent.type === 'header' ? 'Header Text' : 'Footer Text'"
											:required="false"
											:noMarginBottom="true"
											:placeholder="activeComponent.type === 'header' ? 'Enter header text...' : 'Enter footer text...'"
										/>
										<TagInfo 
											@action="tagInfoAction" 
											:textEditorHasDateTag="textEditorHasDateTag" 
											:dateFormatOptions="dateFormatOptions" 
											:dateFormat="componentSettings.dateFormat"
											@dateFormatUpdated="handleDateFormatUpdated"
										/>
									</div>

									<!-- Add text styling options -->
									<div class="setting-group">
										<label class="input-label">Text Alignment</label>
										<v-select
											v-model="componentSettings.headerTextAlignment"
											:options="alignmentOptions.limited"
											:reduce="(option) => option.value"
											label="label"
											:append-to-body="true"
										/>
									</div>

									<!-- Add text styling options -->
									<div class="setting-group">
										<FormInput
											type="number"
											v-model.number="componentSettings.headerTextSize"
											name="headerTextSize"
											label="Text Size"
											:min="1"
											:max="100"
											:required="false"
											placeholder="Select text size"
											:noMarginBottom="true"
										/>
									</div>

									<ColourPicker 
										v-model="componentSettings.headerTextColor"
										title="Text Color"
										default='#000000' 
										class="color-setting" 
									/>

								</template>

								<!-- Graph Settings -->
								<template v-if="activeComponent && activeComponent.type === 'graph'">

									<div class="setting-group">
										<label>Graph Data Type</label>
										<v-select
											v-model="componentSettings.graphType"
											:options="graphTypes"
											:reduce="(option) => option.value"
											label="label"
											@input="handleGraphTypeChange"
											:append-to-body="true"
										/>
									</div>

									<div class="setting-group" v-if="!!componentSettings.graphType && componentSettings.graphType !== 'csv'">
										<label v-if="defaultProjectId.current">{{ labelProject }} (override report default)</label>
										<label v-if="!defaultProjectId.current">{{ labelProject }}</label>
										<v-select
											v-model="componentSettings.selectedProject"
											:options="allProjects"
											:reduce="(option) => option._id"
											label="name"
											:placeholder="altProjectPlaceholder"
											:clearable="!!defaultProjectId.current"
											:searchable="true"
											:append-to-body="true"
										/>
									</div>

									<template v-if="componentSettings.graphType === 'csv'">
										<div class="setting-group">
											<label>Upload CSV Data</label>
											<div class="csv-help-text">
												<p><strong>CSV Format:</strong></p>
												<ul>
													<li>First row: Labels for X-axis (e.g., months, dates)</li>
													<li>Each subsequent row: Numeric values only (or empty)</li>
													<li>Each row will become a separate line on the graph</li>
													<li>
														Adding only one column (excluding config columns) will create a pie chart, otherwise a line
														graph will be created.
													</li>
												</ul>
												<p>
													<strong>Extra Customization:</strong> You can add customization columns that allow you to give a
													custom colour to each line (hex codes only, eg #4e4e4e), or add a label (appears in the legend) to
													each line, an example CSV is <a :href="graphCSVexample" target="_blank">available here</a>. These
													columns must come first.
												</p>
											</div>
											<CsvUploader
												:allowed-headers="[]"
												:required-headers="[]"
												:initial-data="componentSettings.csvData"
												type="graph"
												@upload-success="handleCsvUpload"
												@validation-error="handleCsvValidationError"
											/>
										</div>
									</template>
									<template v-if="showAddXYLabels">
										<div class="setting-group">
											<FormInput
												v-model="componentSettings.csvXAxisLabel"
												name="csvXAxisLabel"
												type="text"
												label="X Axis Label (horizontal)"
												:required="false"
												:noMarginBottom="true"
											/>
										</div>

										<div class="setting-group">
											<FormInput
												v-model="componentSettings.csvYAxisLabel"
												name="csvYAxisLabel"
												type="text"
												label="Y Axis Label (vertical)"
												:required="false"
												:noMarginBottom="true"
											/>
										</div>
									</template>

									<!-- Add new date range settings after graph type -->
									<div class="setting-group" v-if="componentSettings.graphType && componentSettings.graphType !== 'csv'">
										<label v-html="componentSettings.graphType === 'rankTracking' ? 'Source' : 'Metrics'" />
										<v-select
											v-model="componentSettings.selectedMetrics"
											:options="componentSettings.graphType === 'rankTracking' ? keywordMetrics : dataDiveMetrics"
											:reduce="(option) => option.value"
											label="label"
											:multiple="componentSettings.graphType === 'dataDive'"
											:append-to-body="true"
										/>

										<!-- Add this warning message -->
										<div v-if="showKeywordLimitWarning" class="warning-text">
											This will be limited to the 1st 20 results to stop the graph getting messy
										</div>

										<!-- Add this competitor switcher -->
										<div
											v-if="componentSettings.selectedMetrics && componentSettings.graphType === 'rankTracking'"
											class="competitor-toggle"
										>
											<Switcher
												v-model="componentSettings.includeCompetitors"
												switcherLabel="Include competitors"
												:sameColour="true"
												:title="'Toggle to include competitor data in the graph'"
											/>
										</div>

										<!-- Add keywords textarea -->
										<div v-if="componentSettings.selectedMetrics === 'selectKeywords'" class="keywords-input">
											<FormInput
												type="textarea"
												v-model="componentSettings.keywordsList"
												name="keywordsList"
												label="Enter Keywords (one per line)"
												:rows="5"
												:noMarginBottom="true"
												theRef="keywords"
												:autoResize="true"
												:autoResizeMaxHeight="160"
											/>
										</div>

										<!-- Add new tags dropdown -->
										<div
											v-if="
												componentSettings.selectedMetrics === 'byTagAvg' ||
													componentSettings.selectedMetrics === 'byTagKeywords'
											"
											class="tag-select"
										>
											<label>Select Tag(s)</label>
											<v-select
												v-model="componentSettings.selectedTag"
												:options="availableTags"
												:reduce="(option) => option.value"
												label="label"
												:append-to-body="true"
												:multiple="true"
											/>
										</div>

										<!-- Add Caddy Score toggle -->
										<div v-if="showCaddyScoreToggle" class="caddy-score-toggle">
											<Switcher
												v-model="componentSettings.includeCaddyScore"
												switcherLabel="Include Caddy Score"
												:sameColour="true"
												:title="'Toggle to include Caddy Score in the graph'"
											/>

											<!-- Add Caddy Score label input -->
											<div v-if="componentSettings.includeCaddyScore" class="caddy-score-label">
												<FormInput
													type="text"
													v-model="componentSettings.caddyScoreLabel"
													name="caddyScoreLabel"
													label="Replace Caddy Score label with..."
													:noMarginBottom="true"
												/>
											</div>
										</div>

										<!-- Add saved searches dropdown -->
										<div v-if="componentSettings.selectedMetrics === 'savedSearch'" class="saved-search-select">
											<label>Select Saved Search</label>
											<v-select
												v-model="componentSettings.selectedSavedSearch"
												:options="savedSearches"
												:reduce="(option) => option.value"
												label="label"
												:append-to-body="true"
											/>
										</div>
									</div>
								</template>

								<!-- Keyword Table Settings -->
								<template v-if="activeComponent && activeComponent.type === 'table'">

									<div class="setting-group">
										<label>Table Type</label>
										<v-select
											v-model="componentSettings.tableType"
											:options="tableTypes"
											:reduce="(option) => option.value"
											label="label"
											:append-to-body="true"
											@input="handleTableTypeChange"
										/>
									</div>

									<div class="setting-group" v-if="!!componentSettings.tableType && !(activeComponent && activeComponent.type === 'table' && componentSettings.tableType === 'csv')">
										<label v-if="defaultProjectId.current">{{ labelProject }} (override report default)</label>
										<label v-if="!defaultProjectId.current">{{ labelProject }}</label>
										<v-select
											v-model="componentSettings.selectedProject"
											:options="allProjects"
											:reduce="(option) => option._id"
											label="name"
											:placeholder="altProjectPlaceholder"
											:clearable="!!defaultProjectId.current"
											:searchable="true"
											:append-to-body="true"
										/>
									</div>

									<!-- Add inside the settings modal where appropriate -->
									<template v-if="activeComponent && activeComponent.type === 'table' && componentSettings.tableType === 'csv'">
										<div class="setting-group">
											<label>Upload CSV Data</label>
											<CsvUploader
												:allowed-headers="[]"
												:required-headers="[]"
												:initial-data="componentSettings.csvData"
												@upload-success="handleCsvUpload"
											/>
										</div>
									</template>

									<!-- Rank Tracking Settings -->
									<template v-if="componentSettings.tableType === 'rankTracking'">
										<div class="setting-group">
											<label>Source</label>
											<v-select
												v-model="componentSettings.selectedMetrics"
												:options="tableKeywordSources"
												:reduce="(option) => option.value"
												label="label"
												:placeholder="'Leave blank for all keywords (max ' + keywordLimit + ')'"
												:append-to-body="true"
											/>
										</div>

										<!-- Add these new conditional inputs -->
										<template v-if="componentSettings.selectedMetrics">
											<!-- By Tag -->
											<div v-if="componentSettings.selectedMetrics === 'byTag'" class="setting-group">
												<label>Select Tag(s)</label>
												<v-select
													v-model="componentSettings.selectedTag"
													:options="availableTags"
													:reduce="(option) => option.value"
													label="label"
													:append-to-body="true"
													:multiple="true"
												/>
											</div>

											<!-- Saved Search -->
											<div v-if="componentSettings.selectedMetrics === 'savedSearch'" class="setting-group">
												<label>Select Saved Search</label>
												<v-select
													v-model="componentSettings.selectedSavedSearch"
													:options="savedSearches"
													:reduce="(option) => option.value"
													label="label"
													:append-to-body="true"
												/>
											</div>

											<!-- Select Keywords -->
											<div v-if="componentSettings.selectedMetrics === 'selectKeywords'" class="setting-group">
												<FormInput
													type="textarea"
													v-model="componentSettings.keywordsList"
													name="keywordsList"
													label="Enter Keywords (one per line)"
													:rows="5"
													:noMarginBottom="true"
													theRef="keywords"
													:autoResize="true"
													:autoResizeMaxHeight="160"
												/>
											</div>
										</template>
									</template>

									<!-- Data Dive Settings -->
									<template v-if="componentSettings.tableType === 'dataDive'">
										<div class="setting-group">
											<label>Data Type</label>
											<v-select
												v-model="componentSettings.dataDiveType"
												:options="dataDiveTableTypes"
												:reduce="(option) => option.value"
												label="label"
												:append-to-body="true"
												@input="handleDataDiveTypeChange"
											/>
										</div>

										<!-- Add new opportunities filter dropdown -->
										<div class="setting-group" v-if="componentSettings.dataDiveType">
											<label>Opportunity/Custom Filters</label>
											<v-select
												v-model="componentSettings.selectedFilter"
												:options="availableOpportunityFilters"
												placeholder="Leave blank for the default results"
												:reduce="(option) => option.value"
												label="label"
												:append-to-body="true"
											/>
										</div>

										<div class="setting-group" v-if="componentSettings.dataDiveType">
											<Switcher
												v-model="componentSettings.useBrandFilters"
												switcherLabel="Use brand filters"
												:sameColour="true"
												:title="'Toggle to apply brand filters to the table'"
											/>
										</div>
									</template>

									<div class="setting-group">
										<FormInput
											type="number"
											v-model.number="componentSettings.rowLimit"
											name="rowLimit"
											:label="'Number of Rows (max ' + keywordLimit + ')'"
											placeholder="Default: 20"
											:min="1"
											:max="keywordLimit"
											:required="false"
											:noMarginBottom="true"
											@input="validateRowLimit"
										/>
									</div>

									<!-- Add this after the existing table settings -->
									<div
										class="setting-group table-headings-builder"
										v-if="
											componentSettings.tableType &&
												(componentSettings.tableType === 'rankTracking' ||
													(componentSettings.tableType === 'dataDive' && componentSettings.dataDiveType))
										"
									>
										<h4>Table Headings</h4>
										<div class="headings-container">
											<div class="settings-drag-drop-components">
												<h5>Available Headings</h5>
												<draggable
													:list="availableHeadings"
													:group="{ name: 'headings', pull: 'clone', put: false }"
													:sort="false"
													:clone="cloneHeading"
													class="headings-list"
												>
													<div
														v-for="heading in availableHeadings"
														:key="heading.type"
														class="component-item"
														@click="cloneHeading(heading, true)"
													>
														{{ heading.name }}
													</div>
												</draggable>
											</div>

											<div class="settings-drag-drop-canvas">
												<h5>Selected Headings</h5>
												<draggable
													v-model="componentSettings.selectedHeadings"
													:group="'headings'"
													@start="drag = true"
													@end="drag = false"
													class="dragArea list-group"
												>
													<div
														v-for="heading in componentSettings.selectedHeadings"
														:key="heading.id"
														class="dragged-item"
														@click="handleHeadingChange({ added: { heading } })"
													>
														{{ heading.name }}
														<button @click="removeHeading(heading)" class="remove-btn">×</button>
													</div>
												</draggable>
											</div>
										</div>
									</div>

									<div	
										class="settings-group-2-768"
										v-if="
											activeComponent &&
											activeComponent.type === 'table' &&
											!!componentSettings.tableType &&
											(
												( componentSettings.tableType === 'rankTracking' && ( !componentSettings.selectedMetrics || componentSettings.selectedMetrics !== 'savedSearch' ) ) || 
												( componentSettings.tableType === 'dataDive' && !componentSettings.selectedFilter )
											)
										"
									>

										<div class="setting-group">
											<label>Order By</label>
											<v-select
												v-model="componentSettings.orderBy"
												:options="availableHeadings"
												:reduce="(option) => option.type"
												label="name"
												:append-to-body="true"
											/>
										</div>

										<div class="setting-group">
											<label>Order Direction</label>
											<v-select
												v-model="componentSettings.orderDir"
												:options="[{ value: 1, name: 'Ascending' },{ value: -1, name: 'Descending' }]"
												:reduce="(option) => option.value"
												label="name"
												:append-to-body="true"
											/>
										</div>

									</div>

									<!-- Add date range settings at the bottom for tables -->
									<template
										v-if="
											activeComponent &&
											activeComponent.type === 'table' &&
											!!componentSettings.tableType &&
											(componentSettings.tableType === 'rankTracking' || componentSettings.tableType === 'dataDive')
										"
									>
										<div class="setting-group date-range-section">
											<div class="date-range-wrap">
												<label>{{ dateRangeLabel.label }}</label>
												<v-select
													v-model="componentSettings.dateRange"
													:options="filteredDateRangeOptions"
													:reduce="(option) => option.value"
													label="label"
													:placeholder="dateRangeLabel.placeholder"
													@input="handleDateRangeChange" 
													:append-to-body="true"
												/>

												<!-- Add the new compare toggle -->
												<div v-if="componentSettings.tableType === 'dataDive'" class="compare-toggle">
													<Switcher
														v-model="componentSettings.comparePrevious"
														switcherLabel="Compare with previous period"
														:sameColour="true"
														:title="'Toggle to compare with previous period'"
													/>
												</div>
											</div>
										</div>
									</template>
									<ReportTableSettings
										:settings="componentSettings"
										@update="updateTableSettings"
										:defaultSettings="reportSettings"
										:isOverride="true"
									/>
								</template>

								<!-- Cover Page Settings -->
								<template v-if="activeComponent && activeComponent.type === 'cover'">
									<div class="settings-drag-drop">
										<div class="settings-drag-drop-components">
											<h4>Available Elements</h4>
											<draggable
												:list="coverPageAvailableElements"
												:group="{ name: 'coverElements', pull: 'clone', put: false }"
												:sort="false"
												:clone="cloneCoverElement"
												@start="drag = true"
												@end="drag = false"
											>
												<div
													v-for="element in coverPageAvailableElements"
													:key="element.id"
													class="component-item"
													@click="cloneCoverElement(element, true)"
												>
													{{ element.name }}
												</div>
											</draggable>
										</div>

										<div class="settings-drag-drop-canvas">
											<h4>Cover Page Layout</h4>
											<draggable
												v-model="componentSettings.coverElements"
												:group="'coverElements'"
												@start="drag = true"
												@end="drag = false"
												@change="handleCoverElementChange"
												class="dragArea list-group"
											>
												<div
													v-for="element in componentSettings.coverElements"
													:key="element.id"
													class="dragged-item"
													@click="openElementSettings(element)"
												>
													<span>{{ element.name }}</span>
													<div class="element-controls">
														<!-- Show logo preview if it exists -->
														<div
															v-if="element.type === 'logo' && element.value && element.value.thumbnail"
															class="logo-preview-mini"
														>
															<img :src="element.value.thumbnail" alt="" />
														</div>
														<button @click.stop="removeCoverElement(element)" class="remove-btn">×</button>
													</div>
												</div>
											</draggable>
										</div>
									</div>
								</template>

								<!-- Add this after other component templates -->
								<template v-if="activeComponent && activeComponent.type === 'metrics'">
									<div class="settings-drag-drop">
										<div class="settings-drag-drop-components">
											<h4>Available Sources</h4>
											<draggable
												:list="metricsAvailableSources"
												:group="{ name: 'metricsSources', pull: 'clone', put: false }"
												:sort="false"
												:clone="cloneMetricsSource"
												@start="drag = true"
												@end="drag = false"
											>
												<div
													v-for="source in metricsAvailableSources"
													:key="source.id"
													class="component-item"
													@click="cloneMetricsSource(source, true)"
												>
													{{ source.name }}
												</div>
											</draggable>
										</div>

										<div class="settings-drag-drop-canvas">
											<h4>Selected Sources</h4>
											<draggable
												v-model="componentSettings.selectedSources"
												:group="'metricsSources'"
												@start="drag = true"
												@end="drag = false"
												@change="handleMetricsSourceChange"
												class="dragArea list-group"
											>
												<div
													v-for="source in componentSettings.selectedSources"
													:key="source.id"
													class="dragged-item"
													@click="openMetricsSourceSettings(source)"
												>
													<span>{{ source.settings.customLabel || source.name }}</span>
													<div class="element-controls">
														<button @click.stop="removeMetricsSource(source)" class="remove-btn">×</button>
													</div>
												</div>
											</draggable>
										</div>
									</div>
								</template>

								<!-- Add date range settings at the bottom for graphs -->
								<template
									v-if="
										activeComponent &&
											activeComponent.type === 'graph' &&
											!!componentSettings.graphType &&
											componentSettings.graphType != 'csv'
									"
								>
									<div class="setting-group date-range-section">
										<h4>Date Range Settings</h4>
										<div class="date-range-wrap">
											<label>{{ dateRangeLabel.label }}</label>
											<v-select
												v-model="componentSettings.dateRange"
												:options="filteredDateRangeOptions"
												:reduce="(option) => option.value"
												:placeholder="dateRangeLabel.placeholder"
												label="label"
												@input="handleDateRangeChange" 
												:append-to-body="true"
											/>

											<!-- Custom Day Count input -->
											<div v-if="componentSettings.dateRange === 'customDays'" class="custom-days-input">
												<FormInput
													type="number"
													v-model.number="componentSettings.customDayCount"
													name="customDayCount"
													label="The last ... Days"
													:min="1"
													:required="false"
													:noMarginBottom="true"
												/>
											</div>

											<!-- Custom Date Range picker -->
											<div v-if="componentSettings.dateRange === 'customRange'" class="custom-date-range">
												<DateFilter
													v-model="componentSettings.customDateRange"
													placeholder="Select date range"
													mode="range"
													@change="handleCustomDateChange"
												/>
											</div>

											<!-- Add the new compare toggle -->
											<div v-if="componentSettings.graphType === 'dataDive'" class="compare-toggle">
												<Switcher
													v-model="componentSettings.comparePrevious"
													switcherLabel="Compare with previous period"
													:sameColour="true"
													:title="'Toggle to compare with previous period'"
												/>
											</div>
										</div>
									</div>
								</template>

								<!-- Text Block Settings -->
								<template v-if="activeComponent && activeComponent.type === 'text'">
									<div class="setting-group">
										<label class="input-label">Text Content</label>
										<SpecialCharacters />
										<VueEditor
											v-model="componentSettings.textContent"
											:editorOptions="editorOptions"
											placeholder="Enter your text here..."
										/>
										<TagInfo 
											@action="tagInfoAction" 
											:textEditorHasDateTag="textEditorHasDateTag" 
											:dateFormatOptions="dateFormatOptions" 
											:dateFormat="componentSettings.dateFormat"
											@dateFormatUpdated="handleDateFormatUpdated"
										/>

									</div>
									<div class="settings-group">

										<hr />

										<!-- Override Defaults section -->
										<h4><span v-html="'Override Font Settings'" /> <span class="toggle-link" @click="toggledTextFontSettings=!toggledTextFontSettings" v-html="toggledTextFontSettings?'&lt;&lt; Hide &gt;&gt;':'&lt;&lt; Click here to view/edit &gt;&gt;'" /></h4>

										<div :class="{'toggled':true}" v-if="toggledTextFontSettings">

											<ReportFontSettings 
												varName="componentSettings" 
												:componentSettings="componentSettings" 
												:reportSettings="reportSettings" 
												@updated="updateSetting" 
											/>

										</div>

									</div>
								</template>

								<!-- Line Break Settings -->
								<template v-if="activeComponent && activeComponent.type === 'lineBreak'">
									<div class="setting-group">
										<FormInput
											type="number"
											v-model.number="componentSettings.lineCount"
											name="lineCount"
											label="Number of line breaks"
											:min="1"
											:required="false"
											:noMarginBottom="true"
										/>
									</div>
								</template>

								<!-- Add this inside the settings modal, alongside other component templates -->
								<template v-if="activeComponent && activeComponent.type === 'newPage'">
									<div class="setting-group">
										<!-- Background Images section - Moved to top -->
										<div class="background-images-section">
											<h4 v-html="componentSettings.backgroundImages.length > 0 ? 'Background Images' : 'Add Background Images'" />
											<draggable
												v-model="componentSettings.backgroundImages"
												class="background-images-list"
												handle=".drag-handle"
												v-if="componentSettings.backgroundImages && componentSettings.backgroundImages.length > 0"
											>
												<div
													v-for="(image, index) in componentSettings.backgroundImages"
													:key="index"
													class="background-image-item"
												>
													<div class="drag-handle" v-if="componentSettings.backgroundImages.length > 1">
														<Icon type="up-down" class="drag-handle-icon" />
													</div>
													<div class="image-preview">
														<img :src="image.thumbnail" alt="Background Image" />
													</div>
													<div class="image-settings">
														<div class="setting-row">
															<FormInput
																type="number"
																v-model.number="image.left"
																name="leftPosition"
																label="Distance from Left (%)"
																:min="0"
																:max="100"
																:required="false"
																:noMarginBottom="true"
															/>
															<FormInput
																type="number"
																v-model.number="image.top"
																name="topPosition"
																label="Distance from Top (%)"
																:min="0"
																:max="100"
																:required="false"
																:noMarginBottom="true"
															/>
														</div>
														<div class="setting-row">
															<div class="size-setting">
																<label>Size Type</label>
																<v-select
																	v-model="image.sizeType"
																	:options="[
																		{ label: 'Width', value: 'width' },
																		{ label: 'Height', value: 'height' },
																	]"
																	:reduce="(option) => option.value"
																	:clearable="false"
																	:searchable="false"
																/>
															</div>
															<FormInput
																type="number"
																v-model.number="image[image.sizeType]"
																:name="image.sizeType"
																:label="`${image.sizeType.charAt(0).toUpperCase() + image.sizeType.slice(1)} (%)`"
																:min="0"
																:max="100"
																:required="false"
																:noMarginBottom="true"
															/>
														</div>
													</div>
													<button @click="removeBackgroundImage(index)" class="remove-btn">×</button>
												</div>
											</draggable>

											<!-- Add image button -->
											<span class="large-button mini no-margin" @click="showBackgroundImageUploader = true">
												Add Image
											</span>
										</div>

										<!-- Override Defaults section -->
										<h4 class="override-title"><span v-html="'Override Defaults On This Page'" /> <span class="toggle-link" @click="toggledPageBreakSettings=!toggledPageBreakSettings" v-html="toggledPageBreakSettings?'&lt;&lt; Hide &gt;&gt;':'&lt;&lt; Click here to view/edit &gt;&gt;'" /></h4>

										<div :class="{'toggled':true,'toggled-page-break':toggledPageBreakSettings}" v-if="toggledPageBreakSettings">

											<div class="colors-row">
												
												<ColourPicker 
													v-model="componentSettings.backgroundColor"
													title="Background Color"
													position="top-right" 
													:default="reportSettings.backgroundColor || '#ffffff'"
													class="color-setting" 
												/>
												
											</div>

											<ReportFontSettings 
												varName="componentSettings" 
												:componentSettings="componentSettings" 
												:reportSettings="reportSettings" 
												@updated="updateSetting" 
											/>

										</div>
									</div>
								</template>

								<GraphTextSettings
									:settings="componentSettings"
									@update="updateTableSettings"
									:defaultSettings="reportSettings"
									:isOverride="true"
									v-if="activeComponent && activeComponent.type === 'graph'"
								/>

								<ReportComponentDims
									v-if="activeComponent && canSetDims.indexOf(activeComponent.type) >= 0"
									:componentSettings="componentSettings"
									:alignmentOptions="reportComponentDimsConfig.alignmentOptions"
									:ignoreInputs="reportComponentDimsConfig.ignoreInputs"
									:onlyInputs="reportComponentDimsConfig.onlyInputs" 
									:overrideInputs="reportComponentDimsConfig.overrideInputs"
								/>
							</div>
						</form>
					</div>
					<div class="modal-actions">
						<span class="large-button mini" @click="saveSettings">Save &amp; Close</span>
						<span class="large-button danger mini" @click="deleteComponent" v-if="!['header', 'footer'].includes(activeComponent.type)">Delete Component</span>
					</div>
				</div>
			</div>
		</div>

		<div v-if="showTextEditorModal" class="modal custom-text-editor has-modal-actions">
			<div class="modal-wrap">
				<div class="modal-inner">
					<h2 class="modal-title">
						Edit Custom Text 
						<span class="close" @click="saveCustomText">X</span>
					</h2>

					<div class="modal-inner-scroll">
						<div class="setting-group">
							<SpecialCharacters />
							<VueEditor 
								v-model="activeCustomTextElement.value" 
								:editorOptions="editorOptions" 
								placeholder="Enter your text here..." 
							/>
							<TagInfo 
								@action="tagInfoAction" 
								:textEditorHasDateTag="textEditorHasDateTag" 
								:dateFormatOptions="dateFormatOptions" 
								:dateFormat="activeCustomTextElement.dateFormat"
								@dateFormatUpdated="handleDateFormatUpdated"
							/>
						</div>

						<ReportComponentDims
							:componentSettings="activeCustomTextElement"
							:alignmentOptions="reportComponentDimsConfig.alignmentOptions"
							:ignoreInputs="['alignment']"
							:onlyInputs="reportComponentDimsConfig.onlyInputs" 
							:overrideInputs="reportComponentDimsConfig.overrideInputs" 
						/>

						<ReportFontSettings 
							varName="activeCustomTextElement" 
							:componentSettings="activeCustomTextElement" 
							:reportSettings="reportSettings" 
							@updated="updateSetting" 
						/>

					</div>
					<div class="modal-actions">
						<span class="large-button mini" @click="saveCustomText">Save Text</span>
						<span class="large-button flip-colours mini" @click="closeTextEditor">Cancel</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Report Settings Modal -->
		<div v-if="showReportSettingsModal" class="modal report-settings-modal has-modal-actions">
			<div class="modal-wrap">
				<div class="modal-inner">
					<h2 class="modal-title">
						Report Settings
						<span class="close" @click="closeReportSettings">X</span>
					</h2>

					<div class="modal-inner-scroll">
						<!-- Replace the existing report settings content with this new layout -->
						

						<!-- Colors section -->
						<div class="setting-group colors-row">

							<div class="setting-group landscape-toggle">
								<Switcher
									v-model="reportSettings.landscape"
									:sameColour="true"
									:title="'Toggle between Portrait and Landscape'"
									:switcherLabel2="'Landscape'"
									:switcherLabel="'Portrait'"
								/>
							</div>
						
							<ColourPicker 
								v-model="reportSettings.backgroundColor"
								title="Background Color"
								position="bottom-right"  
								class="color-setting" 
							/>
							
						</div>

						<ReportFontSettings 
							varName="reportSettings" 
							:componentSettings="reportSettings" 
							:reportSettings="reportSettings" 
							@updated="updateSetting" 
						/>

						<ReportComponentDims 
							:componentSettings="reportSettings"
							:alignmentOptions="reportComponentDimsConfig.alignmentOptions"
							:ignoreInputs="reportComponentDimsConfig.ignoreInputs"
							:onlyInputs="reportComponentDimsConfig.onlyInputs" 
							:overrideInputs="reportComponentDimsConfig.overrideInputs" 
							:title="'Page Margins (% of page width)'"
							type="report"
							description="Margins are set as a percentage of the page width. Maximum 25%."
						/>

						<ReportTableSettings 
							:settings="reportSettings" 
							@update="updateTableSettings" 
							:isOverride="false" 
						/>

						<GraphTextSettings :settings="reportSettings" @update="updateTableSettings" :isOverride="false" />

					
					</div>
					<div class="modal-actions">
						<span class="large-button mini" @click="saveReportSettings">Save Settings</span>
					</div>
				</div>
			</div>
		</div>

		<!-- Add new modals for element settings -->
		<div v-if="showElementSettingsModal" class="modal element-settings-modal has-modal-actions">
			<div class="modal-wrap">
				<div class="modal-inner">
					<h2 class="modal-title">
						{{ activeElement ? activeElement.name + " Settings" : "Element Settings" }}
						<span class="close" @click="closeElementSettings">×</span>
					</h2>

					<div class="modal-inner-scroll">
						<!-- Logo Settings -->
						<template v-if="activeElement && activeElement.type === 'logo'">
							<div class="setting-group">
								<ImagePreview 
									:value="activeElement.value"
									@show-uploader="showLogoUploader = true"
									@show-size-info="showImageSizeInfo"
									@delete-image="deleteImage"
								>
									<strong>The image will be 20% of the width of the page by default, you can override this below!</strong><br>
									<span class="large-button mini flip-colours" @click="showImageSizeInfo">Set Image Size below!!</span><br><br>
									<strong><em>Note: If you have set the width/height below and you are seeing blank pages it's because the image is too large for the page, this removes the image but creates blank pages!</em></strong>
								</ImagePreview>
							</div>
							
							<ReportComponentDims
								:componentSettings="activeElement"
								:alignmentOptions="reportComponentDimsConfig.alignmentOptions"
								:ignoreInputs="reportComponentDimsConfig.ignoreInputs"
								:onlyInputs="reportComponentDimsConfig.onlyInputs"
								type="Image"
								:hideToggle="true"
								:overrideInputs="{
									height: {
										label: 'Height % (ignores width)',
									},
									width: {
										placeholder: !activeElement.height ? '20' : '',
										highlight: this.highlightImageSizeInfoWidth
									}
								}"
								:toggleIt="toggleImageSizeInfo"
							/>
						</template>

						<!-- Project Name Settings -->
						<template v-if="activeElement && activeElement.type === 'project'">
							<div class="setting-group">
								<FormInput
									type="number"
									v-model="activeElement.fontSize"
									name="fontSize"
									label="Font Size"
									:placeholder="`Defaults to H2 size`"
									:min="8"
									:max="100"
									:required="false"
									:noMarginBottom="true"
									:step="1"
								/>
							</div>

							<ReportComponentDims 
								:componentSettings="activeElement" 
								:alignmentOptions="reportComponentDimsConfig.alignmentOptions" 
								:ignoreInputs="reportComponentDimsConfig.ignoreInputs"
								:onlyInputs="reportComponentDimsConfig.onlyInputs"
								:overrideInputs="reportComponentDimsConfig.overrideInputs"
							/>
						</template>

						<!-- Date Settings -->
						<template v-if="activeElement && activeElement.type === 'date'">
							<div class="setting-group">
								<label class="input-label">Date Format</label>
								<v-select
									v-model="activeElement.dateFormat"
									:options="dateFormatOptions"
									:reduce="(option) => option.value"
									label="label"
									@input="handleDateFormatChange($event, activeElement)"
									class="date-format-select"
									placeholder="Select date format"
									:append-to-body="true"
								/>
							</div>

							<div class="setting-group">
								<FormInput
									type="number"
									v-model="activeElement.fontSize"
									name="fontSize"
									label="Font Size"
									:placeholder="`Defaults to H4 size`"
									:min="8"
									:max="100"
									:required="false"
									:noMarginBottom="true"
									:step="1"
								/>
							</div>
							<ReportComponentDims 
								:ignoreInputs="reportComponentDimsConfig.ignoreInputs"
								:onlyInputs="reportComponentDimsConfig.onlyInputs"
								:overrideInputs="reportComponentDimsConfig.overrideInputs"
								:componentSettings="activeElement"
								:alignmentOptions="reportComponentDimsConfig.alignmentOptions"
							/>
						</template>
					</div>
					<div class="modal-actions">
						<span class="large-button mini" @click="saveElementSettings">Save Settings</span>
						<span class="large-button flip-colours mini" @click="closeElementSettings">Cancel</span>
					</div>
				</div>
			</div>
		</div>

		<ReportCSVcustom 
			v-if="showCSVcustomModal" 
			:reportCSV="reportCSV" 
			@close="showCSVcustomModal=false" 
			@save-report-csv="saveReportCSV" 
		/>

		<!-- Add this new modal after your other modals -->
		<div v-if="showMetricsSourceModal" class="modal metrics-source-modal has-modal-actions">
			<div class="modal-wrap">
				<div class="modal-inner">
					<h2 class="modal-title">
						{{ activeMetricsSource ? activeMetricsSource.name + " Settings" : "Metrics Source Settings" }}
						<span class="close" @click="closeMetricsSourceSettings">×</span>
					</h2>

					<div class="modal-inner-scroll">
						<!-- Add project selector before other settings -->
						<div class="setting-group" v-if="activeMetricsSource && ['rankTracking', 'dataDive'].includes(activeMetricsSource.type)">
							<label v-if="defaultProjectId.current">{{ labelProject }} (override report default)</label>
							<label v-if="!defaultProjectId.current">{{ labelProject }}</label>
							<v-select
								v-model="activeMetricsSource.settings.selectedProject"
								:options="allProjects"
								:reduce="(option) => option._id"
								label="name"
								:placeholder="altProjectPlaceholder"
								:clearable="!!defaultProjectId.current"
								:searchable="true"
								:append-to-body="true"
							/>
						</div>

						<TagInfo 
							@action="tagInfoAction" 
							:textEditorHasDateTag="textEditorHasDateTag" 
							:dateFormatOptions="dateFormatOptions" 
							:dateFormat="activeMetricsSource.settings.dateFormat"
							@dateFormatUpdated="handleDateFormatUpdated"
							:metric="activeMetricsSource.type"
							v-if="activeMetricsSource.type === 'custom'"
						/>

						<!-- Existing settings continue below -->
						<div class="setting-group">
							<FormInput
								type="text"
								v-model="activeMetricsSource.settings.customLabel"
								name="customLabel"
								label="Custom Label (just for your reference in the list)"
								:required="false"
								:noMarginBottom="true"
								placeholder="Enter custom label..."
							/>
						</div>

						<div class="setting-group" v-if="(activeMetricsSource.type === 'rankTracking' || activeMetricsSource.type === 'dataDive')">
							<label>Select Metric</label>
							<v-select
								v-model="activeMetricsSource.settings.selectedMetric"
								:options="getMetricsOptions(activeMetricsSource.type)"
								:reduce="(option) => option.value"
								label="label"
								:searchable="false"
								:clearable="false"
							/>
						</div>

						<div
							class="setting-group"
						>
							<FormInput
								type="text"
								v-model="activeMetricsSource.settings.metricLabel"
								name="metricLabel"
								:label="activeMetricsSource.type === 'custom'?'Metric Label':'Override Metric Label'" 
								:required="false"
								:noMarginBottom="true"
								:placeholder="activeMetricsSource.type === 'custom'?'':'Defaults to selected metric name'"
							/>
						</div>

						<TagInfo 
							@action="tagInfoAction" 
							:textEditorHasDateTag="textEditorHasDateTag" 
							:dateFormatOptions="dateFormatOptions" 
							:dateFormat="activeMetricsSource.settings.dateFormat"
							@dateFormatUpdated="handleDateFormatUpdated"
							:metric="activeMetricsSource.type"
							v-if="activeMetricsSource.type !== 'custom'"
						/>

						<div
							class="setting-group"
							v-if="
								activeMetricsSource &&
									activeMetricsSource.type === 'custom' 
							"
						>
							<FormInput
								type="text"
								v-model="activeMetricsSource.settings.selectedMetric"
								name="selectedMetric"
								label="Value" 
								:required="false"
								:noMarginBottom="true"
							/>
						</div>

						<div class="setting-group">
							<Switcher
								v-model="activeMetricsSource.settings.shortenNumbers"
								switcherLabel2="Shorten numbers to 1.2K, 3M etc"
								:sameColour="true"
								:title="'Toggle to shorten large numbers in a more readable format'"
							/>
						</div>

						<div
							class="setting-group"
							v-if="
								activeMetricsSource &&
									(activeMetricsSource.type === 'rankTracking' || activeMetricsSource.type === 'dataDive') &&
									activeMetricsSource.settings.selectedMetric 

							"
						>
							<Switcher
								v-model="activeMetricsSource.settings.comparisonPeriod"
								switcherLabel2="Compare to previous period?"
								:sameColour="true"
							/>
						</div>

						<div
							class="setting-group"
							v-if="
								activeMetricsSource &&
									(activeMetricsSource.type === 'rankTracking' || activeMetricsSource.type === 'dataDive') &&
									activeMetricsSource.settings.selectedMetric &&
									!!activeMetricsSource.settings.comparisonPeriod 
							"
						>
							<label>Period</label>
							<v-select
								v-model="activeMetricsSource.settings.period"
								:options="comparisonPeriodOptions"
								:reduce="(option) => option.value"
								:clearable="false"
							/>
						</div>

						<div
							class="setting-group"
							v-if="
								activeMetricsSource &&
									activeMetricsSource.type === 'custom' 
							"
						>
							<FormInput
								type="text"
								v-model="activeMetricsSource.settings.comparisonPeriod"
								name="comparisonPeriod"
								label="Previous Value"
								:required="false"
								:noMarginBottom="true"
							/>
						</div>

						<div
							class="setting-group"
							v-if="
								activeMetricsSource &&
									activeMetricsSource.type === 'custom' && 
									!!activeMetricsSource.settings.comparisonPeriod
							"
						>
							<label>Add An Arrow?</label>
							<v-select
								v-model="activeMetricsSource.settings.arrowDirection"
								:options="arrowDirectionOptions"
								:reduce="(option) => option.value"
								label="label"
								placeholder="Leave blank for no arrow"
								:clearable="true"
								:searchable="false"
								:append-to-body="true"
							/>
						</div>

						<div class="setting-group" v-if="activeMetricsSource.settings.comparisonPeriod && activeMetricsSource.type === 'dataDive' && activeMetricsSource.settings.selectedMetric && !!activeMetricsSource.settings.period">
							<Switcher
								v-model="activeMetricsSource.settings.compareYear"
								:switcherLabel="'Compare to previous '+activeMetricsSource.settings.period.replace('ly','')"
								:switcherLabel2="'Same ' + activeMetricsSource.settings.period.replace('ly','') + ' as last year'"
								:sameColour="true"
								:title="'Toggle to show the change in the metric as a percentage'"
							/>
						</div>

						<div class="setting-group" v-if="activeMetricsSource.settings.comparisonPeriod && activeMetricsSource.settings.selectedMetric">
							<Switcher
								v-model="activeMetricsSource.settings.showAsPercentage"
								switcherLabel2="Show the change as a %?"
								:sameColour="true"
								:title="'Toggle to show the change in the metric as a percentage'"
							/>
						</div>

						<div
							class="setting-group"
							v-if="
								activeMetricsSource && !!activeMetricsSource.settings.comparisonPeriod 
							"
						>
							<FormInput
								type="text"
								v-model="activeMetricsSource.settings.previousValueIncreaseLabel"
								name="previousValueIncreaseLabel"
								:label="(activeMetricsSource.type === 'custom'&&activeMetricsSource.settings.comparisonPeriod)?'Previous Value Increase Label (appends to previous value)':'Previous Value Increase Label'" 
								:required="false"
								:noMarginBottom="true"
							/>
						</div>

						<div
							class="setting-group"
							v-if="
								activeMetricsSource && !!activeMetricsSource.settings.comparisonPeriod 
							"
						>
							<FormInput
								type="text"
								v-model="activeMetricsSource.settings.previousValueDecreaseLabel"
								name="previousValueDecreaseLabel"
								:label="(activeMetricsSource.type === 'custom'&&activeMetricsSource.settings.comparisonPeriod)?'Previous Value Decrease Label (appends to previous value)':'Previous Value Decrease Label'" 
								:required="false"
								:noMarginBottom="true"
							/>
						</div>

						<div
							class="setting-group"
							v-if="
								activeMetricsSource && !!activeMetricsSource.settings.comparisonPeriod 
							"
						>
							<FormInput
								type="text"
								v-model="activeMetricsSource.settings.previousValueNoChangeLabel"
								name="previousValueNoChangeLabel"
								:label="(activeMetricsSource.type === 'custom'&&activeMetricsSource.settings.comparisonPeriod)?'Previous Value No Change Label (appends to previous value)':'Previous Value No Change Label'" 
								:required="false"
								:noMarginBottom="true"
							/>
						</div>
					</div>
					<div class="modal-actions">
						<span class="large-button mini" @click="saveMetricsSourceSettings">Save Settings</span>
						<span class="large-button flip-colours mini" @click="closeMetricsSourceSettings">Cancel</span>
					</div>
				</div>
			</div>
		</div>

		<ImageManager
			v-if="showBackgroundImageUploader"
			@close="showBackgroundImageUploader = false"
			:selectable="true"
			:showIcon="false"
			@selected="handleBackgroundImageUploaderClose"
		/>

		<ImageManager
			v-if="showLogoUploader"
			@close="showLogoUploader = false"
			:selectable="true"
			:autoSelectUploaded="true" 
			@selected="handleLogoUploaderClose"
		/>
		
	</div>
</template>

<script>
import Vue from "vue";
import { mapMutations, mapState } from "vuex";
import draggable from "vuedraggable";
import { VueEditor } from "vue2-editor";
import FormInput from "../components/Form/Input.vue";
import vSelect from "vue-select";
import Switcher from "../components/Switcher.vue";
import ApiService from "./../services/api.service";
import general from "./../utils/general";
import DateFilter from "../components/Data/DateFilter.vue";
import Icon from "../components/Display/Icon.vue";
import ImageManager from "../components/ImageManager.vue";
import CsvUploader from "../components/CsvUploader.vue";
import ReportTableSettings from "../components/Report/ReportTableSettings.vue";
import ReportComponentDims from "../components/Report/ReportComponentDims.vue";
import ReportFontSettings from "../components/Report/ReportFontSettings.vue";
import GraphTextSettings from "../components/Report/GraphTextSettings.vue";
import ReportCSVcustom from "../components/Report/ReportCSVcustom.vue";
import TagInfo from "../components/Report/TagInfo.vue";
import ImagePreview from '../components/Report/ImagePreview.vue'
import SpecialCharacters from '../components/Report/SpecialCharacters.vue'
import ColourPicker from "../components/Display/ColourPicker.vue";
export default {
	name: "ReportBuilder",
	components: {
		VueEditor,
		draggable,
		FormInput,
		vSelect,
		Switcher,
		DateFilter,
		Icon,
		ImageManager,
		CsvUploader,
		ReportTableSettings,
		ReportComponentDims,
		ReportFontSettings,
		GraphTextSettings,
		ReportCSVcustom,
		TagInfo,
		ImagePreview,
		SpecialCharacters,
		ColourPicker
	},
	data() {
		return {
			previewWindow: null,
			toggleImageSizeInfo: 0,
			highlightImageSizeInfoWidth: false,
			friendlyErrors: [],
			toggledPageBreakSettings: false,
			toggledTextFontSettings: false,
			flushCache: false,
			projectIdIsDataDive: false,
			projectId2: null,
			availableComponents: [
				{ id: "metrics", name: "Top Level Metrics", type: "metrics", setDims: true, icon: 'report-metrics' },
				{ id: "cover", name: "Cover Page", type: "cover", icon: 'report-cover' },
				{ id: "graph", name: "Graph", type: "graph", setDims: true, icon: 'report-graph' },
				{ id: "table", name: "Table", type: "table", setDims: true, icon: 'report-table' },
				{ id: "text", name: "Text Block", type: "text", setDims: true, icon: 'report-text' },
				{ id: "newPage", name: "Page Break", type: "newPage", icon: 'report-page' },
				{ id: "header", name: "Header", type: "header" },
				{ id: "footer", name: "Footer", type: "footer" }
			],
			generatingReport: false,
			savingReport: false,
			reportName: "",
			reportItems: [],
			showSettingsModal: false,
			keywordLimit: 1000,
			reportCSV: {},
			componentSettings: {},
			tableHeadings: {
				dataDive: {
					url: [
						{ type: "url", name: "URL" },
						{ type: "sessions", name: "Organic Sessions" },
						{ type: "users", name: "Organic Users" },
						{ type: "bounceRate", name: "Bounce Rt. %" },
						{ type: "avgSessionDuration", name: "Avg Sess. MM:SS" },
						{ type: "clicks", name: "Clicks" },
						{ type: "ctr", name: "CTR %" },
						{ type: "impressions", name: "Impressions" },
						{ type: "position", name: "Avg Pos." },
					],
					keyword: [
						{ type: "keyword", name: "Keyword" },
						{ type: "url", name: "URL" },
						{ type: "clicks", name: "Clicks" },
						{ type: "ctr", name: "CTR %" },
						{ type: "impressions", name: "Impressions" },
						{ type: "position", name: "Avg Pos." },
					],
				},
				rankTracking: [
					{ type: "keyword", name: "Keyword" },
					{ type: "currentRank", name: "Ranking" },
					{ type: "volume", name: "Search Vol." },
					{ type: "bestRank", name: "Best" },
					{ type: "firstRank", name: "First" },
					{ type: "url", name: "URL" },
					{ type: "rankUpdated", name: "Updated" },
					{ type: "lastConflict", name: "Last Cannibalisation" },
					{ type: "frequencyName", name: "Frequency" },
					{ type: "features", name: "Features" },
				],
			},
			activeComponent: null,
			graphTypes: [
				{ label: "Rank Tracking", value: "rankTracking" },
				{ label: "Data Dive", value: "dataDive" },
				{ label: "CSV Upload", value: "csv" },
			],
			keywordMetrics: [
				{ label: "Average Rank (entire project)", value: "avgRank" },
				{ label: "By Tag (average of all keywords)", value: "byTagAvg" },
				{ label: "By Tag (line per keyword)", value: "byTagKeywords" },
				{ label: "Saved Search", value: "savedSearch" },
				{ label: "Manually Enter Keyword(s)", value: "selectKeywords" },
			],
			dataDiveMetrics: [
				{ label: "Organic Sessions", value: "sessions" },
				{ label: "Organic Users", value: "users" },
				{ label: "Bounce Rate %", value: "bounceRate" },
				{ label: "Avg Session Duration", value: "avgSessionDuration" },
				{ label: "Clicks", value: "clicks" },
				{ label: "CTR %", value: "ctr" },
				{ label: "Impressions", value: "impressions" },
				{ label: "Average Position", value: "position" },
			],
			showLogoUploader: false,
			savedSearches: [],
			optionsByProject: {},
			coverPageAvailableElements: [
				{ id: "logo", name: "Image", type: "logo" },
				{ id: "customText", name: "Custom Text", type: "customText" },
			],
			dateFormatOptions: [
				{ label: "March 19, " + new Date().getFullYear(), value: "F j, Y" },
				{ label: "March 19th, "+new Date().getFullYear(), value: "F jS, Y" },
				{ label: "19 March "+new Date().getFullYear(), value: "j F Y" },
				{ label: "19th March "+new Date().getFullYear(), value: "jS F Y" },
				{ label: "March", value: "F" },
				{ label: "March 19", value: "F j" },
				{ label: "March 19th", value: "F jS" },
				{ label: "19 March", value: "j F" },
				{ label: "19th March", value: "jS F" },
				{ label: "03/19/"+new Date().getFullYear(), value: "m/d/Y" },
				{ label: "19/03/"+new Date().getFullYear(), value: "d/m/Y" },
				{ label: new Date().getFullYear()+"-03-19", value: "Y-m-d" },
				{ label: new Date().getFullYear()+"-19-03", value: "Y-d-m" },
			],
			dataDiveTableTypes: [
				{ label: "URL", value: "url" },
				{ label: "Keyword", value: "keyword" },
			],
			tableTypes: [
				{ label: "Rank Tracking", value: "rankTracking" },
				{ label: "Data Dive", value: "dataDive" },
				{ label: "CSV Upload (max 1000 rows)", value: "csv" },
			],
			validationErrors: {
				logo: false,
				dateFormat: false,
				tag: false,
				savedSearch: false,
				keywords: false,
				metrics: false,
				dataType: false,
				headings: false,
			},
			dateRangeOptions: [
				{ label: "1 Day", value: 1, types:['rankTracking'], components:['table'] },
				{ label: "7 Days", value: 7, types:['rankTracking','dataDive'], components:['graph','table'] },
				{ label: "30 Days", value: 30, types:['rankTracking','dataDive'], components:['graph','table'] },
				{ label: "90 Days", value: 90, types:['dataDive'], components:['graph','table'] },
				{ label: "Custom Day Count", value: "customDays", types:['rankTracking'], components:['graph'] },
				{ label: "Custom Date Range", value: "customRange", types:['rankTracking'], components:['graph'] },
			],
			tableKeywordSources: [
				{ label: "All Keywords", value: "all" },
				{ label: "By Tag", value: "byTag" },
				{ label: "Saved Search", value: "savedSearch" },
				{ label: "Manually Enter Keyword(s)", value: "selectKeywords" },
			],
			editorOptions: {
				theme: "snow",
				formats: ["bold", "italic", "strike", "align", "header", "list", "link", "blockquote", "script", "divider", "table", "color", "background"],
				modules: {
					clipboard: {
						matchVisual: false,
					},
					toolbar: {
						container: [
							["bold", "italic", "strike"],
							[{ header: [1, 2, 3, 4, 5, 6, false] }],
							//[{ color: [] }, { background: [] }],
							[{ align: ["", "center", "right", "justify"] }],
							[{ list: "ordered" }, { list: "bullet" }],
							["link"],
							["blockquote"],
							[{ script: "sub" }, { script: "super" }],
							["divider"],
							["table"],
							["clean"],
						]
					},
				},
				clipboard: {
					matchVisual: false,
				},
				keyboard: {
					bindings: {},
				},
				styles: {
					html: { margin: "0", padding: "0" },
					p: { margin: "0", padding: "0" },
					h1: { margin: "0 0 40px 0", padding: "0" },
					h2: { margin: "0 0 0 1.5em", padding: "0" },
					h3: { margin: "0 0 0 1.5em", padding: "0" },
					h4: { margin: "0 0 0 1.5em", padding: "0" },
					h5: { margin: "0 0 0 1.5em", padding: "0" },
					h6: { margin: "0 0 0 1.5em", padding: "0" },
				},
				formats: {
					align: {
						attribute: "style",
						prefix: "text-align: ",
						suffix: ";",
					},
					bold: {
						tag: "strong",
						attribute: "style",
						prefix: "font-weight: ",
						suffix: ";",
					},
					italic: {
						tag: "em",
						attribute: "style",
						prefix: "font-style: ",
						suffix: ";",
					},
					header: {
						attribute: "style",
						prefix: "font-size: ",
						suffix: "px;",
					},
					color: {
						attribute: "style",
						prefix: "color: ",
						suffix: ";",
					},
					background: {
						attribute: "style",
						prefix: "background-color: ",
						suffix: ";",
					}
				},
			},
			theReportId: null,
			showTextEditorModal: false,
			activeCustomTextElement: null,
			reportSettings: {
				backgroundColor: '#ffffff',
				textColor: '#333333'
			},
			showReportSettingsModal: false,
			showElementSettingsModal: false,
			activeElement: null,
			showBackgroundImageUploader: false,
			isNewComponent: false,
			showCSVcustomModal: false,
			metricsAvailableSources: [
				{ id: "rankTracking", name: "Rank Tracking", type: "rankTracking" },
				{ id: "dataDive", name: "Data Dive", type: "dataDive" },
				{ id: "custom", name: "Custom", type: "custom" },
			],
			showMetricsSourceModal: false,
			activeMetricsSource: null,
			dataDiveMetricsOptions: [
				{ label: "Organic Sessions", value: "sessions" },
				{ label: "Organic Users", value: "users" },
				{ label: "Bounce Rate", value: "bounceRate" },
				{ label: "Avg Sess. Dur.", value: "avgSessionDuration" },
				{ label: "Clicks", value: "clicks" },
				{ label: "Impressions", value: "impressions" },
				{ label: "CTR %", value: "ctr" },
				{ label: "Avg Position", value: "position" },
			],
			rankTrackingMetricsOptions: [
				{ label: "Active Keywords", value: "keywordCount" },
				{ label: "Caddy Score", value: "rankCaddyScore" },
				{ label: "Average Rank", value: "averageRank" },
				{ label: "In Top 3", value: "inTop3" },
				{ label: "In Top 10", value: "inTop10" },
				{ label: "In Top 50", value: "inTop50" },
				{ label: "Climbed", value: "goneUp" },
				{ label: "Fallen", value: "goneDown" },
				{ label: "Unchanged", value: "unChanged" },
			],
			comparisonPeriodOptions: [
				{ label: "Week", value: "weekly" },
				{ label: "Month", value: "monthly" },
				{ label: "Quarter", value: "quarterly" },
			],
			arrowDirectionOptions: [
				{ label: 'Yes, increase is positive', value: 'positive' },
				{ label: 'Yes, increase is negative', value: 'negative' }
			],
		};
	},
	watch: {
		componentSettings: {
			deep: true,
			handler(newVal) { 
				
				this.isNewComponent = false;
				if (!!this.componentSettings.selectedProject && !this.optionsByProject[this.componentSettings.selectedProject]) {
					this.getOptionsByProject(this.componentSettings.selectedProject);
				}
				this.buildQuillCSS ();
			},
		},
		activeCustomTextElement: {
			deep: true,
			handler(newVal) { 
				this.buildQuillCSS ();
			},
		},
	},
	computed: {
		...mapState("general", ["projectList", "projectListLoaded"]),

		year() {
			return new Date().getFullYear();
		},

		textEditorHasDateTag () {

			if ( this.activeComponent ) {

				let f = '';

				if ( this.activeComponent.type === 'text' ) {

					f = 'textContent';

				}

				if (this.activeComponent.type === 'header' || this.activeComponent.type === 'footer') {

					f = 'headerText';

				}

				if ( f ) return this.componentSettings[f].includes('[[date]]');

			}

			if ( this.showMetricsSourceModal ) {

				let fields = ['metricLabel'], foundDate = false;

				if ( this.activeMetricsSource.type === 'custom' ) {

					fields = ['metricLabel','selectedMetric','comparisonPeriod'];

				}

				fields.forEach ( function (field) {
					
					if ( this.activeMetricsSource.settings[field] && this.activeMetricsSource.settings[field].includes('[[date]]') ) {

						foundDate = true;

					}

				}.bind(this));

				return foundDate;

			}

			return this.showTextEditorModal && this.activeCustomTextElement.value.includes('[[date]]');

		},

		reportItemsHasPageBreakAt () {

			const d = this.reportItems.findIndex(item => item.type === 'newPage');

			return d > -1 ?  ( d + 1 ) : -1;

		},

		reportItemsEmpty() {
			let c = 0;
			this.reportItems.forEach(item => {
				if ( item.type !== 'header' && item.type !== 'footer' ) {
					c++;
				}
			});
			return c === 0;
		},

		activeComponentTypeTitle () {

			return ( !!this.activeComponent.type && ['header', 'footer'].includes(this.activeComponent.type) ) ? general.ucFirst(this.activeComponent.type) : ( this.activeComponent.name || 'Component' );

		},

		reportComponentDimsConfig () {

			let ignoreInputs = [];
			let onlyInputs = [];
			let overrideInputs = {};
			let alignmentOptions = 'all';

			if ( this.showReportSettingsModal ) {
				ignoreInputs.push('width');
				ignoreInputs.push('height');
				ignoreInputs.push('alignment');
			}
			else {

				if ( this.activeComponent ) {
					switch ( this.activeComponent.type ) {

						case 'metrics':

							ignoreInputs.push('width');
							ignoreInputs.push('height');
							//ignoreInputs.push('alignment');
							alignmentOptions = 'absoluteOnly';
							overrideInputs.width = {
								label: "Width (of each metric, % of page width)",
								placeholder: "Blank for auto"
							};

							break;

					}

				}

				if ( this.activeElement ) {
					switch ( this.activeElement.type ) {

						case 'date':

							if ( !this.showElementSettingsModal ) break;

							ignoreInputs.push('width');
							ignoreInputs.push('height');

							break;

					}

				}

			}

			return { ignoreInputs, onlyInputs, overrideInputs, alignmentOptions: this.alignmentOptions[alignmentOptions] };

		},

		alignmentOptions() {
			let ops = {
				all: [
					{ label: "Left", value: "left" },
					{ label: "Center", value: "center" },
					{ label: "Right", value: "right" },
					{ label: "Absolute", value: "absolute" },
				],
				limited: [
					{ label: "Left", value: "left" },
					{ label: "Center", value: "center" },
					{ label: "Right", value: "right" },
				],
				absoluteOnly: [
					{ label: "Absolute", value: "absolute" },
				],
			};

			return ops;
		},

		availableComponentsList () {

			return this.availableComponents.filter(c => c.type !== 'header' && c.type !== 'footer');

		},

		canSetDims() {
			let a = [];
			this.availableComponents.forEach((c) => {
				if (c.setDims) {
					a.push(c.type);
				}
			});
			return a;
		},

		selectedProject() {
			if ( this.activeMetricsSource ) {

				return this.activeMetricsSource.settings.selectedProject || this.defaultProjectId.current || null;

			}
			return this.componentSettings.selectedProject || this.defaultProjectId.current || null;
		},

		availableTags() {
			if ( !this.selectedProject ) return [];
			return this.optionsByProject[this.selectedProject] && this.optionsByProject[this.selectedProject].availableTags
				? this.optionsByProject[this.selectedProject].availableTags
				: [];
		},

		opportunitiesFilters() {
			if ( !this.selectedProject ) return [];
			return this.optionsByProject[this.selectedProject] && this.optionsByProject[this.selectedProject].opportunitiesFilters
				? this.optionsByProject[this.selectedProject].opportunitiesFilters
				: {};
		},
		availableOpportunityFilters() {
			if (!this.componentSettings.dataDiveType || !this.opportunitiesFilters[this.componentSettings.dataDiveType]) {
				return [];
			}
			return this.opportunitiesFilters[this.componentSettings.dataDiveType];
		},

		allProjects() {

			let list = [], result = [];

			if ( !this.projectListLoaded ) return list;

			if ( this.currentComponentType === 'rankTracking' ) {

				result = this.projectList.filter(project => (project._id !== this.defaultProjectId.current && project.hasRankTracking ));

			}
			else {

				result = this.projectList.filter(project => (project._id !== this.defaultProjectId.current && project.hasDataDive && project.isDataDivePrimary ));

			}

			result.forEach(project => {

				list.push({
					name: this.currentComponentType === 'rankTracking' ? project.name : new URL( project.url ).hostname,
					_id: project._id
				});

			});

			return list;
		},

		altProjectPlaceholder () {
			const p = this.projectName;
			return this.defaultProjectId.current ? ( p ? `Defaults to ${p}` : `Leave blank to use report default` ) : ( 'Please select a ' + this.projectName );
		},

		projectName() {
			if ( this.defaultProjectId.current ) {

				const project = this.projectList.find(project => project._id === this.defaultProjectId.current);

				if ( project ) {
					return this.currentComponentType === 'rankTracking' ? project.name : new URL( project.url ).hostname;
				}
				
			}
			return this.currentComponentType === 'rankTracking' ? 'Project' : 'Domain';
		},

		currentComponentType () {

			if ( this.activeMetricsSource ) {

				return this.activeMetricsSource.type === 'custom' ? 'rankTracking' : this.activeMetricsSource.type;

			}

			if ( this.activeComponent ) {

				switch ( this.activeComponent.type ) {

					case 'graph':
						return this.componentSettings.graphType;
					case 'table':
						return this.componentSettings.tableType;
					case 'metrics':
						return this.componentSettings.metricsType;
					case 'text':
						return this.componentSettings.textType;
					case 'header':
				}

			}

			return 'rankTracking';

		},

		uploadFormData() {
			let f = { type: "image", vars: {} };

			return f;
		},
		showCaddyScoreToggle() {
			return (
				this.componentSettings.graphType === "rankTracking" &&
				(this.componentSettings.selectedMetrics === "avgRank" || this.componentSettings.selectedMetrics === "byTagAvg")
			);
		},
		reportId() {
			return this.$route.params.reportId || this.theReportId;
		},
		projectId() {
			return this.$route.params.projectId;
		},
		defaultProjectId() {
			let r = {
				current: null,
				rankTracking: null,
				dataDive: null,
			};
			if ( this.projectIdIsDataDive ) {
				r.dataDive = this.projectId;
				r.rankTracking = this.projectId2;
			}
			else {
				r.rankTracking = this.projectId;
				r.dataDive = this.projectId2;
			}
			r.current = r[this.currentComponentType];
			return r;
		},
		labelProject() {
			return this.currentComponentType === 'rankTracking' ? 'Project' : 'Domain';
		},
		isFirstItem() {
			return this.activeComponent && this.reportItems.length > 0 && this.reportItems[0].id === this.activeComponent.id;
		},
		showKeywordLimitWarning() {
			return (
				this.componentSettings.graphType === "rankTracking" &&
				["byTag", "savedSearch", "selectKeywords"].includes(this.componentSettings.selectedMetrics)
			);
		},
		availableHeadings() {
			if (!this.componentSettings.tableType) return [];

			if (this.componentSettings.tableType === "rankTracking") {
				return this.tableHeadings.rankTracking;
			}

			if (this.componentSettings.tableType === "dataDive") {
				return this.componentSettings.dataDiveType === "url" ? this.tableHeadings.dataDive.url : this.tableHeadings.dataDive.keyword;
			}

			return [];
		},
		dateRangeLabel () {
			const f = this.activeComponent.type === 'graph' ? 'graphType' : 'tableType';
			let label = 'Date Range', placeholder = '';
			switch ( this.activeComponent.type ) {
				case 'graph':
					label = 'Date Range';
					placeholder = 'Select a date range';
					break;
				case 'table':
					label = this.componentSettings[f] === 'dataDive' ? 'Date Range' : 'Compare With Previous';
					placeholder =this.componentSettings[f] === 'dataDive' ? '' : 'No comparison';
			}
			return { label, placeholder };
		},
		
		filteredDateRangeOptions() {
			return this.dateRangeOptions.filter(function(option) {
				if ( !option.components.includes(this.activeComponent.type)) {
					return false;
				}
				const f = this.activeComponent.type === 'graph' ? 'graphType' : 'tableType';
				return option.types.includes(this.componentSettings[f]);
			}.bind(this));
		},
		graphCSVexample() {
			return "https://rankcaddy.io/wp-content/uploads/2024/12/report-graph-csv-example.csv";
		},
		showAddXYLabels () {
			let headerCount = 0;
			if (this.activeComponent && this.activeComponent.type === "graph" && this.componentSettings.graphType === 'csv' && this.componentSettings.csvData ) {
				
				this.componentSettings.csvData.headers.forEach(header => {
					if ( header.substring(0,7) !== 'config:' ) {
						headerCount++;
					}
				});
			}
			return headerCount > 1;
		}
	},
	methods: {
		...mapMutations("general", ["updateLoadingText"]),

		updateSetting(evt) {
			Vue.set( this[evt.varName], evt.key, evt.value );
			this.buildQuillCSS ();
		},

		showImageSizeInfo() {
			this.highlightImageSizeInfoWidth = true;
			setTimeout(() => {
				general.scrollIntoView('report-component-dim-width',true);
			}, 100);
		},

		handleDateFormatUpdated (dateFormat) {

			let f = '';

			if ( this.activeComponent && this.activeComponent.type === 'text' ) {

				f = 'componentSettings';

			}

			if ( this.showTextEditorModal ) {

				f = 'activeCustomTextElement';

			}

			if ( f ) Vue.set(this[f], 'dateFormat', dateFormat);

		},
		tagInfoAction(info) {
			
			if ( info === 'addCustomTags') {

				this.showCSVcustomModal = true;

			}
			
		},
		reportItemIcon(item) {
			return this.availableComponents.find(c => c.type === item.type).icon;
		},
		updateTableSettings(settings) {
			let t = settings.isOverride ? "componentSettings" : "reportSettings";
			settings.updated.forEach(
				function(u) {
					Vue.set(this[t], u, settings.value[u]);
				}.bind(this)
			);
		},
		cloneComponent(item, pushIt) { 
			// Check if report name is empty
			if (!this.reportName.trim()) {
				this.$snotify.warning("Please name your report before adding components");
				return;
			}

			let d = Date.now(),
				item2 = {
				...item,
				id: `${item.type}_${d}`, // Ensure unique ID for new items
			};
			if (!!pushIt) {
				this.reportItems.push(item2);
				this.isNewComponent = true;
				this.handleChange({ added: { element: { ...item2, id: item2.type + "_" + d } } });
			} else {
				return item2;
			}
		},
		saveReportCSV(csvData) {
			this.reportCSV = csvData;
			this.reportSettings.updated = new Date();
			this.saveReport(['csv']); // Add this line to save the report after updating CSV data
		},
		buildQuillCSS () {
			
			const styleId = 'quill-dynamic-styles';
			let styleElement = document.getElementById(styleId);
			let cssContent = '';
			const pre = '#quill-container .ql-editor';
			const c = this.showTextEditorModal ? this.activeCustomTextElement : this.componentSettings;
			for ( let i = 1; i <= 6; i++ ) {
				let fontSize = c[`h${i}Size`] || this.reportSettings[`h${i}Size`] || '';
				if ( fontSize ) {
					cssContent += `${pre} h${i} { font-size: ${fontSize}px; }`;
				}
				let color = c[`h${i}Color`] || this.reportSettings[`h${i}Color`] || '';
				if ( color ) {
					cssContent += `${pre} h${i} { color: ${color}; }`;
				}
			}

			let baseFontSize = c.fontSize || this.reportSettings.fontSize || '';
			if ( baseFontSize ) {
				cssContent += `${pre} p { font-size: ${baseFontSize}px; }`;
				cssContent += `${pre} { font-size: ${baseFontSize}px; }`;
			}

			let font = c.font || this.reportSettings.font || '';
			if ( font ) {
				cssContent += `${pre} p { font-family: ${font}; }`;
				cssContent += `${pre} { font-family: ${font}; }`;
			}

			let color = c.textColor || this.reportSettings.textColor || '';
			if ( color ) {
				cssContent += `${pre} p { color: ${color}; }`;
				cssContent += `${pre} { color: ${color}; }`;
			}

			if (!styleElement) {
				styleElement = document.createElement('style');
				styleElement.id = styleId;
				document.head.appendChild(styleElement);
			}

			styleElement.textContent = cssContent;

		},
		async fontCSS () {

			const styleId = 'report-font-css';
			let styleElement = document.getElementById(styleId);

			if (!styleElement) {
				styleElement = document.createElement('style');
				styleElement.id = styleId;
				document.head.appendChild(styleElement);
				try {
					const response = await ApiService.get(`reports/fonts`);
					styleElement.textContent = response.data;
				} 
				catch (error) {
					console.error("Error fetching fonts:", error);
				}
			}

		},
		handleChange(evt) {
			
			if (evt.added) {
				Vue.set(this, "activeComponent", general.stripObject ( evt.added.element ) );

				let isNew = this.isNewComponent;

				this.componentFields ( 'all', 'componentSettings', {}, true );

				// Set the basic info
				this.componentSettings.componentId = this.activeComponent.id;
				this.componentSettings.name = this.activeComponent.name;
				
				// Load existing settings if they exist
				if (this.activeComponent.settings) {
					const settings = general.stripObject ( this.activeComponent.settings );

					// Load common settings
					this.componentSettings.name = settings.name;

					this.componentFields ( this.activeComponent.type, null, settings, false, 'componentSettings' );
					
				}

				this.showSettingsModal = true;
				setTimeout(
					function() {
						this.isNewComponent = isNew;
					}.bind(this),
					100
				);
			}
		},
		closeSettingsModal() {
			this.showSettingsModal = false;
			this.activeComponent = null;
			this.isNewComponent = false;
			this.componentFields ( 'all', 'componentSettings', {}, true );
		},
		componentFields ( type, subType = null, values = {}, clear = false, updateThis = '' ) {

			let dimsComponents = { components: {  } };

			this.canSetDims.forEach(type => {
				dimsComponents.components[type] = '';
			});

			if ( ['all', 'tableTypeChange','dataDiveTypeChange'].includes(type) && !!subType && !!clear ) {

				updateThis = subType;

			}

			let fields = {
				name: { components: { all: '' } },
				componentId: { components: { all: null } },
				
				logo: { components: { header: null, footer: null, cover: null } },
				graphType: { components: { graph: null } },
				selectedMetrics: { components: { graph: null, table: null } },
				selectedKeywords: { components: { table: null, tableTypeChange: null } },
				includeCaddyScore: { components: { graph: false, graphTypeChange: false } },
				caddyScoreLabel: { components: { graph: '', graphTypeChange: '' } },
				selectedTag: { components: { graph: [], table: [], graphTypeChange: [] } },
				keywordsList: { components: { graph: '', table: '', graphTypeChange: '' } },
				displayOnEveryPage:{ components: { header: false, footer: false } },
				coverText: { components: { cover: '' } },
				coverElements: { components: { cover: [] } },
				tableType: { components: { table: null } },
				orderBy: { components: { table: '', tableTypeChange: '', dataDiveTypeChange: '' } },
				orderDir: { components: { table: '', tableTypeChange: '', dataDiveTypeChange: '' } },
				dataDiveType: { components: { table: null, tableTypeChange: null } },
				rowLimit: { components: { table: null, tableTypeChange: null } },
				selectedSavedSearch: { components: { graph: [], table: [], graphTypeChange: [] } },
				includeCompetitors: { components: { graph: false, graphTypeChange: false } },
				selectedFilter: { components: { table: null, tableTypeChange: null, dataDiveTypeChange: null } },
				selectedHeadings: { components: { table: [], tableTypeChange: [], dataDiveTypeChange: [] } },
				dateRange: { components: { graph: null, table: null, graphTypeChange: null } },
				customDayCount: { components: { graph: null, graphTypeChange: null } },
				customDateRange: { components: { graph: null, graphTypeChange: null } },
				comparePrevious: { components: { graph: false, table: false } },
				textContent: { components: { text: '' } },
				dateFormat: { components: { text: this.dateFormatOptions[0].value } },
				lineCount: { components: { lineBreak: '' } },
				useBrandFilters: { components: { graph: false, table: false, dataDiveTypeChange: false } },
				selectedProject: { components: { graph: null, table: null, newPage: null, metrics: null, tableTypeChange: null } },
				backgroundImages: { components: { newPage: [] } },
				backgroundColor: { components: { newPage: null } },
				textColor: { components: { newPage: null, text: null } },
				font: { components: { newPage: null, text: null } },
				fontSize: { components: { newPage: '' } },
				h1Size: { components: { newPage: null, text: null } },
				h2Size: { components: { newPage: null, text: null } },
				h3Size: { components: { newPage: null, text: null } },
				h4Size: { components: { newPage: null, text: null } },
				h5Size: { components: { newPage: null, text: null } },
				h6Size: { components: { newPage: null, text: null } },	
				h1Color: { components: { newPage: null, text: null } },
				h2Color: { components: { newPage: null, text: null } },
				h3Color: { components: { newPage: null, text: null } },
				h4Color: { components: { newPage: null, text: null } },
				h5Color: { components: { newPage: null, text: null } },
				h6Color: { components: { newPage: null, text: null } },
				csvData: { components: { graph: null, table: null, graphTypeChange: null } },
				marginTop: dimsComponents,
				marginLeft: dimsComponents,
				marginRight: dimsComponents,
				marginBottom: dimsComponents,
				width: dimsComponents,
				height: dimsComponents,
				alignment: dimsComponents,
				csvXAxisLabel: { components: { graph: null } },
				csvYAxisLabel: { components: { graph: null } },
				graphTitle: { components: { graph: '' } },
				titleFontSize: { components: { graph: '' } },
				legendFontSize: { components: { graph: '' } },
				legendColor: { components: { graph: '' } },
				axisFontSize: { components: { graph: '' } },
				axisColor: { components: { graph: '' } },
				logoWidth: { components: { header: null, footer: null } },
				logoHeight: { components: { header: null, footer: null } },
				logoLeft: { components: { header: null, footer: null } },
				logoRight: { components: { header: null, footer: null } },
				headerText: { components: { header: '', footer: '' } },
				headerTextSize: { components: { header: 14, footer: 14 } },
				headerTextAlignment: { components: { header: "right", footer: "right" } },
				displayPagination: { components: { header: false, footer: false } },
				headerTextColor: { components: { header: "#000000", footer: "#000000" } },
				selectedSources: { components: { metrics: [] } },
				tableOuterBorderColor: { components: { table: null } },
				tableBorderColor: { components: { table: null } },
				tableHeaderTextColor: { components: { table: null } },
				tableHeaderBgColor: { components: { table: null } },
				tableRowTextColor: { components: { table: null } },
				tableRowBgColor: { components: { table: null } },
				tableAltRowTextColor: { components: { table: null } },
				tableAltRowBgColor: { components: { table: null } },
				tableHeaderFontSize: { components: { table: null } },
				tableOuterBorderWidth: { components: { table: null } },
				tableBorderWidth: { components: { table: null } },
				tableRowFontSize: { components: { table: null } },
				customLabel: { components: { metrics: '' } },
				selectedMetric: { components: { metrics: null } },
				metricLabel:  { components: { metrics: '' } },
				shortenNumbers: { components: { metrics: false } },
				comparisonPeriod: { components: { metrics: subType === 'custom' ? '' : false } },
				period:  { components: { metrics: 'weekly' } },
				arrowDirection: { components: { metrics: null } },
				compareYear: { components: { metrics: false } },
				showAsPercentage: { components: { metrics: false } },
				previousValueIncreaseLabel: { components: { metrics: '' } },
				previousValueDecreaseLabel: { components: { metrics: '' } },
				previousValueNoChangeLabel: { components: { metrics: '' } },
			};

			let processed = {};
			
			Object.keys(fields).forEach(function ( fieldName ) { 
				if ( typeof fields[fieldName].components[type] !== 'undefined' || type === 'all' ) {
					const type2 = type === 'all' ? Object.keys(fields[fieldName].components)[0] : type;
					processed[fieldName] = values[fieldName] || fields[fieldName].components[type2];
					if ( updateThis ) {
						Vue.set( this[updateThis], fieldName, general.stripObject ( processed[fieldName] ) );
					}
				}
			}.bind(this));

			return general.stripObject ( processed );

		},
		async saveSettings(event, deleteIfNew = false) {
			if (!this.componentSettings.name) {
				this.$snotify.warning("All components need a name");
				return;
			}

			// Validate settings before saving
			const { isValid, errorMessages } = this.validateSettings();
			
			if (!isValid) {
				if (deleteIfNew && this.isNewComponent) {
					return this.deleteComponent();
				} else {
					general.$snotify(this, "html-error", null, errorMessages.map((msg) => `• ${msg}`).join("<br>"));
					return;
				}
			}

			console.log({...this.componentSettings});

			const index = this.reportItems.findIndex((item) => item.id === this.componentSettings.componentId);
	
			if (index > -1) {
				// Update basic properties
				Vue.set( this.reportItems, index, {
					...this.reportItems[index],
					name: this.componentSettings.name,
					updated: new Date(),
					settings: {
						name: this.componentSettings.name,
						...this.componentFields(this.reportItems[index].type, null, this.componentSettings),
						...(this.reportItems[index].type === "metrics" && {
							selectedSources: this.componentSettings.selectedSources.map(source => ({
								...source,
								settings: {
									...source.settings,
									...this.componentFields("metrics", source.type || null, source.settings )
								}
							})),
						}),
					},
				});
			}
			else {

				console.error('Component not found',general.stripObject ( this.componentSettings),general.stripObject ( this.reportItems ));

			}

			this.closeSettingsModal();

			// Save the report after updating component settings
			try {
				
				await this.saveReport(['config']);

			} catch (error) {
				this.$snotify.error("Failed to save report");
				console.error("Error saving report:", error);
			}
		},
		handleLogoUploaderClose(response) {
			this.showLogoUploader = false;
			if (response && response.thumbnail) {
				if (this.activeComponent && this.activeComponent.type === "cover") {
					const elId = this.activeElement ? this.activeElement.id : null;
					// Find the active logo element in coverElements
					const logoElementIndex = this.componentSettings.coverElements.findIndex(
						(element) => element.type === "logo" && element.id === elId
					);

					if (logoElementIndex !== -1) {
						// Update the specific logo element
						Vue.set(this.componentSettings.coverElements[logoElementIndex], "value", response);
						Vue.set(this.activeElement, "value", response);
					}
				} else {
					// For other components (like header), update the component settings directly
					this.componentSettings.logo = response;
				}
			}
		},
		handleGraphTypeChange() {
			if (this.componentSettings.graphType === "rankTracking") {
				Vue.set(this.componentSettings, "selectedMetrics", null);
			} else if (this.componentSettings.graphType === "dataDive") {
				Vue.set(this.componentSettings, "selectedMetrics", []);
			}

			this.componentFields ( 'graphTypeChange', 'componentSettings', {}, true );

		},
		cloneCoverElement(element, pushIt) {
			const newElement = {
				...element,
				id: `${element.type}_${Date.now()}`,
				value: element.type === "customText" ? "" : null,
				dateFormat: null,
				alignment: "left", // Add default alignment
				fontSize: '',
			};
			if (pushIt) {
				if (!this.componentSettings.coverElements) {
					Vue.set(this.componentSettings, "coverElements", []);
				}
				this.componentSettings.coverElements.push(newElement);
				this.handleCoverElementChange({ added: { element: newElement } });
			} else {
				return newElement;
			}
		},
		handleCoverElementChange(evt) {
			if (!this.componentSettings.coverElements) {
				this.componentSettings.coverElements = [];
			}

			if (evt.added) {
				const element = evt.added.element;

				// Handle custom text elements with existing logic
				if (element.type === "customText") {
					this.activeCustomTextElement = element;
					if ( !this.activeCustomTextElement.dateFormat) {
						this.activeCustomTextElement.dateFormat = this.dateFormatOptions[0].value;
					}
					this.showTextEditorModal = true;
				}
				// Automatically open settings modal for other configurable elements
				else if (["project", "date", "logo"].includes(element.type)) {
					this.activeElement = element;
					this.showElementSettingsModal = true;
				}
			}
		},
		deleteImage() {
			if (this.activeComponent && this.activeComponent.type === "cover") {
				const elId = this.activeElement ? this.activeElement.id : null;
				// Find the active logo element in coverElements
				const logoElementIndex = this.componentSettings.coverElements.findIndex(
					(element) => element.type === "logo" && element.id === elId
				);

				if (logoElementIndex !== -1) {
					// Update the specific logo element
					Vue.set(this.componentSettings.coverElements[logoElementIndex], "value", null);
					Vue.set(this.activeElement, "value", null);
				}
			} else {
				Vue.set(this.componentSettings, "logo", null);
			}
		},
		removeCoverElement(element) {
			if (!this.componentSettings.coverElements) return;

			const index = this.componentSettings.coverElements.findIndex((el) => el.id === element.id);
			if (index !== -1) {
				this.componentSettings.coverElements.splice(index, 1);
			}
		},
		handleDateFormatChange(format, element) {
			// Update the element's date format
			element.dateFormat = format;
		},
		validateRowLimit(value) {
			if (!value) return; // Allow empty value

			// Convert to number
			let numValue = parseInt(value);

			// If value exceeds 1000, reset to 1000 and show notification
			if (numValue > 1000) {
				this.$snotify.warning("Maximum row limit is 1000. Value has been adjusted.");
				this.$nextTick(() => {
					this.componentSettings.rowLimit = 1000;
				});
				return;
			}

			// Ensure minimum of 1
			numValue = Math.max(1, numValue);

			// Update the value
			this.$nextTick(() => {
				this.componentSettings.rowLimit = numValue;
			});
		},
		handleTableTypeChange() {
			this.componentFields ( 'tableTypeChange', 'componentSettings', {}, true );
		},
		handleDataDiveTypeChange() {
			this.componentFields ( 'dataDiveTypeChange', 'componentSettings', {}, true );
		},
		handleHeadingChange(evt) {
			if (!this.componentSettings.selectedHeadings) {
				this.componentSettings.selectedHeadings = [];
			}
		},
		cloneHeading(heading, pushIt) {
			const newElement = {
				...heading,
				id: `${heading.type}`,
			};
			const exists = this.componentSettings.selectedHeadings.some((h) => h.type === heading.type);
			if (exists) {
				this.$snotify.warning("This column has already been added");
				return false; // Prevent the clone
			}
			if (pushIt) {
				if (!this.componentSettings.selectedHeadings) {
					Vue.set(this.componentSettings, "selectedHeadings", []);
				}
				this.componentSettings.selectedHeadings.push(newElement);
				this.handleHeadingChange({ added: { element: newElement } });
			} else {
				return newElement;
			}
		},
		removeHeading(heading) {
			if (!this.componentSettings.selectedHeadings) return;

			const index = this.componentSettings.selectedHeadings.findIndex((h) => h.id === heading.id);
			if (index !== -1) {
				this.componentSettings.selectedHeadings.splice(index, 1);
			}
		},
		validateSettings() {
			let isValid = true;
			const errorMessages = [];
			const cs = this.componentSettings;

			if ( !this.activeComponent ) {
				return { isValid, errorMessages }; // Return early if no type selected
			}

			const ac = this.activeComponent;

			function msg (msg) {
				errorMessages.push(msg);
				isValid = false;
			}

			function a (n) {
				return Array.isArray(n) ? n.length > 0 : ( typeof n === 'string' ? n.trim() !== "" : !!n );
			}

			switch ( ac.type ) {

				case "graph":

					if (!cs.graphType) {
						msg("Please select a Graph Data Type");
						return { isValid, errorMessages }; // Return early if no type selected
					}

					if ( !this.selectedProject ) {
						msg("Please select a project");
						return { isValid, errorMessages }; // Return early if no type selected
					}

					switch ( cs.graphType ) {

						case "rankTracking":

							if (!cs.selectedMetrics) {

								msg("Please select a source");
								break;

							}

							switch ( cs.selectedMetrics ) {

								case "byTagAvg":
								case "byTagKeywords":
									if ( !a(cs.selectedTag) ) {
										msg("Please select at least one tag");
									}
									break;

								case "savedSearch":
									if (!cs.selectedSavedSearch) {
										msg("Please select a saved search");
									}
									break;

								case "selectKeywords":
									if (!cs.keywordsList.trim()) {
										msg("Please enter at least one keyword");
									}
									break;
									
							}

							if (!cs.dateRange) {
								msg("Please select a date range");
							} else {
								if (cs.dateRange === "customDays" && !cs.customDayCount) {
									msg("Please enter the number of days");
								}
								if (cs.dateRange === "customRange" && (!cs.customDateRange || !cs.customDateRange.start || !cs.customDateRange.end)) {
									msg("Please select a custom date range");
								}
							}
							
							break;
						case "dataDive":

							if ( !a(cs.selectedMetrics) ) {
								msg("Please select at least one metric");
								break;
							}

							if (!cs.dateRange) {
								msg("Please select a date range");
							} 

							break;
						case "csv":

							if (!cs.csvData || !Object.keys(cs.csvData).length) {
								msg("Please upload CSV data");
							}

							break;
					}

					break;
				case "table":

					if ( !this.selectedProject ) {
						msg("Please select a project");
						return { isValid, errorMessages }; // Return early if no type selected
					}

					if (!cs.tableType) {
						msg("Please select a Table Type");
						return { isValid, errorMessages }; // Return early if no type selected
					}

					if (cs.tableType !== "csv" && !a(cs.selectedHeadings)) {
						msg("Please select at least one column for the table");
					}

					switch ( cs.tableType ) {

						case "dataDive":
							if ( !a(cs.dataDiveType) ) {
								msg("Please select a data type");
							}
							break;

						case "rankTracking":
							if ( !a(cs.selectedMetrics) && ( !cs.orderBy || !cs.orderDir ) ) {
								msg("Please select a source or order by/direction");
							}
							if ( cs.selectedMetrics === "byTag" && !a(cs.selectedTag) ) {
								msg("Please select at least one tag");
							}
							if ( cs.selectedMetrics === "savedSearch" && !cs.selectedSavedSearch ) {
								msg("Please select a saved search");
							}
							if ( cs.selectedMetrics === "selectKeywords" && !cs.keywordsList.trim() ) {
								msg("Please enter at least one keyword");
							}
							break;
					}

					break;
				case "cover":
					if ( !a(cs.coverElements) ) {
						msg("Please add at least one element to the cover page");
					}

					const logoElement = this.componentSettings.coverElements.find((el) => el.type === "logo");
					const dateElement = this.componentSettings.coverElements.find((el) => el.type === "date");

					if (logoElement && ( !logoElement.value || !logoElement.value._id ) ) {
						msg("Please upload a logo");
					}

					if (dateElement && !dateElement.dateFormat) {
						msg("Please select a date format");
					}
					break;
				case "text":
					if (!cs.textContent.trim()) {
						msg("Please enter some text content");
					}
					break;
			}

			return { isValid, errorMessages };
		},
		handleDateRangeChange() {
			// Reset custom values when date range changes
			if (this.componentSettings.dateRange !== "customDays") {
				this.componentSettings.customDayCount = null;
			}
			if (this.componentSettings.dateRange !== "customRange") {
				this.componentSettings.customDateRange = null;
			}
		},
		handleCustomDateChange(range) {
			this.componentSettings.customDateRange = range;
		},
		async generateReport( getDefinition = false ) {
			try {
				if (this.generatingReport) return;
				let ok = true;
				this.generatingReport = true;
				ok = await this.saveReport([], true);
				this.friendlyErrors = [];
				if (ok.success) {
					if (ok.redirect) {
						this.$router.push(ok.redirect);
						return;
					}

					const getType = "generate";

					let response;
					try {
						response = await ApiService.get(`reports/${this.projectId}/${this.reportId}/${getType}`, { responseType: "arraybuffer" });
					} 
					catch (error) {
    
						if (error.response && error.response.data) {
							// Handle ArrayBuffer response
							this.generatingReport = false;
							if (error.response.data instanceof ArrayBuffer) {
								try {
									// Convert ArrayBuffer to text
									const decoder = new TextDecoder('utf-8');
									const jsonString = decoder.decode(error.response.data);
									const errorData = JSON.parse(jsonString);
									let showGenericMessage = "An error occurred while generating your report";
									// Now we can loop through it if it's an array
									if (Array.isArray(errorData.message)) {
										
										errorData.message.forEach(function(item) {
											if ( typeof item.index !== 'undefined' && !!item.error ) {
												this.friendlyErrors.push(item);
											}
										}.bind(this));
									} else if (errorData.message) {
										showGenericMessage = errorData.message;
									}
									this.$snotify.error(showGenericMessage);
								} catch (decodeError) {
									console.error("Error decoding response:", decodeError);
									this.$snotify.error('An error occurred while processing the PDF');
								}
							}
							// Handle regular array of errors
							else if (Array.isArray(error.response.data)) {
								error.response.data.forEach(function(item) {
									this.$snotify.error(item.message);
								}.bind(this));
							}
							// Handle single error message
							else if (typeof error.response.data === 'object' && error.response.data.message) {
								this.$snotify.error(error.response.data.message);
							}
						} else {
							this.$snotify.error('Failed to generate report');
						}
						return;
					}
					
					const type = response.headers["content-type"];
					const blob = new Blob([response.data], { type: type, encoding: "UTF-8" });
					const url = window.URL.createObjectURL(blob);

					// If preview is requested
					if (getDefinition) {
						let shouldCreateNewWindow = true;

						const wrapperHTML = `
							<!DOCTYPE html>
							<html>
							<head>
								<title>${this.reportName} - Preview</title>
								<style>
									body, html {
										margin: 0;
										padding: 0;
										height: 100%;
										overflow: hidden;
									}
									iframe {
										width: 100%;
										height: 100%;
										border: none;
									}
									.error-message {
										display: none;
										padding: 20px;
										text-align: center;
										color: #666;
									}
									iframe:not([src]) + .error-message {
										display: block;
									}
								</style>
							</head>
							<body>
								<iframe src="${url}" type="application/pdf"></iframe>
								<div class="error-message">Unable to display PDF preview. Please check if you have a PDF viewer installed.</div>
							</body>
							</html>
						`;

						const wrapperBlob = new Blob([wrapperHTML], { type: 'text/html' });
						const wrapperUrl = URL.createObjectURL(wrapperBlob);
						
						// Check if we have an existing window reference and if it's still valid
						if (this.previewWindow) {
							try {
								// This will throw an error if the window was closed
								if (!this.previewWindow.closed) {
									URL.revokeObjectURL(this.previewWindow.location.href); // Clean up old blob URL
									this.previewWindow.location.href = wrapperUrl;
									this.previewWindow.document.title = `${this.reportName} - Preview`;
									shouldCreateNewWindow = false;
								}
							} catch (e) {
								// Window reference is no longer valid
								shouldCreateNewWindow = true;
							}
						}

						// Create new window if needed
						if (shouldCreateNewWindow) {
							if (this.previewWindow) {
								try {
									this.previewWindow.close();
								} catch (e) {
									// Ignore errors if window is already closed
								}
							}
							this.previewWindow = window.open(wrapperUrl, 'pdf-preview');
							if (this.previewWindow) {
								this.previewWindow.document.title = `${this.reportName} - Preview`;
							}
						}

					} else {
						// Download handling
						const link = document.createElement("a");
						link.href = url;
						link.download = this.reportName.replaceAll(" ", "-") + ".pdf";
						link.click();
					}

					// Clean up the URL object after use
					setTimeout(() => {
						window.URL.revokeObjectURL(url);
					}, 100);
				} else {
					this.$snotify.error("Error saving report");
				}
				this.generatingReport = false;
			} catch (error) { console.error(error);
				this.generatingReport = false;
				this.$snotify.error("Sorry, there was an error generating your report, we have been notified and will look into it.");
			}
		},
		async saveReport( fields = [], forGenerate = false ) { // settings, csv, config
			try {
				this.savingReport = true;
				this.friendlyErrors = [];
				let update = {
					config: this.reportItems,
					name: this.reportName,
					settings: this.reportSettings,
					csv: this.reportCSV,
					fields: fields, 
					flushCache: this.flushCache
				};
				this.buildQuillCSS ();
				let response = await ApiService.post(`reports/${this.projectId}` + (this.reportId ? `/${this.reportId}` : ""), update);
				this.loadReportData ( response.data );
				this.savingReport = false;
				this.flushCache = false;
				if (this.previewWindow && !this.previewWindow.closed && !forGenerate) {
					this.previewWindow.document.title = `Updating...`;
					this.generateReport(true);
				}
				return { success: true, redirect: false };
			} catch (error) {
				console.error("Error saving report:", error);
				this.updateLoadingText();
				this.savingReport = false;
				this.$snotify.error("Sorry, there was an error saving your report, we have been notified and will look into it.");
				return { success: false, redirect: false };
			}
		},
		removeItem(event, item, callback) {
			if (event) {
				event.stopPropagation();
			}
			const removeIt = function (item,callback) {
				const index = this.reportItems.findIndex((i) => i.id === item.id);
				if (index > -1) {
					this.reportItems.splice(index, 1);
					this.saveReport(['config']);
				}
				if ( callback ) {
					callback();
				}
			}.bind(this);
			if ( this.isNewComponent ) {
				return removeIt(item,callback);
			}
			this.$dialog
				.confirm( "Are you sure?", { okText: 'Yes', cancelText: 'Cancel'})
				.then(function() {

					removeIt(item,callback);

				}.bind(this))
				.catch(function() {});
		},
		deleteComponent() {
			if (this.activeComponent) {
				this.removeItem(null, { id: this.componentSettings.componentId }, function () {
					this.closeSettingsModal();
					this.$snotify.clear();
				}.bind(this));
			}
			
		},
		saveCustomText() {
			if (this.activeCustomTextElement) {
				// Find and update the element in coverElements
				const elementIndex = this.componentSettings.coverElements.findIndex((el) => el.id === this.activeCustomTextElement.id);
				if (elementIndex !== -1) {
					this.componentSettings.coverElements[elementIndex] = this.activeCustomTextElement;
				}
			}
			this.closeTextEditor();
		},
		closeTextEditor() {
			// If no text was entered, remove the element
			if (this.activeCustomTextElement && !this.activeCustomTextElement.value) {
				this.removeCoverElement(this.activeCustomTextElement);
			}
			this.showTextEditorModal = false;
			this.activeCustomTextElement = null;
		},
		editCustomText(element) {
			this.activeCustomTextElement = { ...element };
			this.showTextEditorModal = true;
		},
		updateColor(color, type) {
			Vue.set(this.reportSettings, type, color.hex);
			this.reportSettings.updated = new Date();
		},
		saveReportSettings() {
			this.showReportSettingsModal = false;
			this.reportSettings.updated = new Date();
			this.saveReport(['settings']); // Save report with updated settings
		},
		closeReportSettings() {
			this.showReportSettingsModal = false;
		},
		openElementSettings(element) {
			// Don't open modal for custom text elements
			if (element.type === "customText") {
				this.editCustomText(element);
				return;
			}

			this.activeElement = { ...element };
			this.showElementSettingsModal = true;
		},
		closeElementSettings() {
			this.showElementSettingsModal = false;
			this.activeElement = null;
		},
		saveElementSettings() {
			if (this.activeElement) {
				const elementIndex = this.componentSettings.coverElements.findIndex((el) => el.id === this.activeElement.id);

				if (elementIndex !== -1) {
					this.componentSettings.coverElements[elementIndex] = {
						...this.componentSettings.coverElements[elementIndex],
						fontSize: this.activeElement.fontSize,
						dateFormat: this.activeElement.dateFormat,
						alignment: this.activeElement.alignment, // Add alignment to saved settings
						width: this.activeElement.width,
						height: this.activeElement.height,
						marginTop: this.activeElement.marginTop,
						marginBottom: this.activeElement.marginBottom,
						marginLeft: this.activeElement.marginLeft,
						marginRight: this.activeElement.marginRight,
					};
				}
			}
			this.closeElementSettings();
		},
		async getOptionsByProject(projectId) {
			Vue.set(this.optionsByProject, projectId, { availableTags: [], opportunitiesFilters: {} });

			// Load tags
			try {
				const response = await ApiService.get("keywords/tags/" + projectId);
				this.optionsByProject[projectId].availableTags = response.data.map((tag) => ({
					label: tag,
					value: tag,
				}));
			} catch (error) {
				console.error("Error loading tags:", error);
				this.$snotify.error("Failed to load tags");
				this.updateLoadingText();
				return false; // Stop execution if tags fail to load
			}

			// Load opportunities filters
			try {
				const opportunitiesFiltersResponse = await ApiService.get(`google/opportunities/filters/${projectId}/url,keyword`);

				let a = {};

				Object.keys(opportunitiesFiltersResponse.data).forEach(
					function(type) {
						a[type] = [];

						Object.keys(opportunitiesFiltersResponse.data[type]).forEach(
							function(filterType) {
								opportunitiesFiltersResponse.data[type][filterType].forEach(
									function(filter) {
										a[type].push({
											label: `${filterType === "preset" ? "Preset" : "Custom"} - ${filter.name}`,
											type: filterType,
											value: filterType === "preset" ? filter.unique : filter._id,
										});
									}.bind(this)
								);
							}.bind(this)
						);
					}.bind(this)
				);

				Vue.set(this.optionsByProject[projectId], "opportunitiesFilters", a);
			} catch (error) {
				console.error("Error loading opportunities filters:", error);
				this.$snotify.error("Failed to load opportunities filters");
				this.updateLoadingText();
				return false; // Stop execution if opportunities filters fail to load
			}

			return true;
		},
		updatePageColor(color, type, toggleName) {
			this.componentSettings[type] = color.hex;
			if ( toggleName ) {
				//this[toggleName] = false;
			}
		},
		handleBackgroundImageUploaderClose(response) {
			this.showBackgroundImageUploader = false;
			if (response && response.thumbnail) {
				// Initialize the background images array if it doesn't exist
				if (!this.componentSettings.backgroundImages) {
					Vue.set(this.componentSettings, "backgroundImages", []);
				}

				// Add the new image with default settings
				this.componentSettings.backgroundImages.push({
					...response,
					left: 0,
					top: 0,
					sizeType: "width",
					width: 100,
					height: null,
				});
			}
		},
		removeBackgroundImage(index) {
			this.componentSettings.backgroundImages.splice(index, 1);
		},
		handleCsvUpload(data) {
			// Only save CSV data if the active component is a table
			if (this.activeComponent && (this.activeComponent.type === "table" || this.activeComponent.type === "graph")) {
				this.componentSettings.csvData = {
					headers: data.headers,
					data: data.data,
				};
			}
		},

		handleCsvValidationError(error) {
			this.$snotify.error("CSV format invalid. Headers should be labels, and all other cells must contain numeric values.");
		},
		cloneMetricsSource(source, pushIt) { 
			const newSource = {
				...general.stripObject ( source ),
				id: `${source.type}_${Date.now()}`,
				settings: this.componentFields ( 'metric', source.type || null )
			};
			
			if (pushIt) {
				if (!this.componentSettings.selectedSources) {
					Vue.set(this.componentSettings, 'selectedSources', []);
				}
				this.componentSettings.selectedSources.push(newSource);
				this.openMetricsSourceSettings(newSource);
			} else {
				return newSource;
			}
		},

		handleMetricsSourceChange(evt) {
			if (!this.componentSettings.selectedSources) {
				this.componentSettings.selectedSources = [];
			}

			if (evt.added) {
				const source = evt.added.element;
				this.openMetricsSourceSettings(source);
			}
		},

		removeMetricsSource(source) {
			if (!this.componentSettings.selectedSources) return;

			const index = this.componentSettings.selectedSources.findIndex((s) => s.id === source.id);
			if (index !== -1) {
				this.componentSettings.selectedSources.splice(index, 1);
			}
		},

		openMetricsSourceSettings(source) {
			this.activeMetricsSource = { ...source };
			this.showMetricsSourceModal = true;
		},
		closeMetricsSourceSettings() {
			// If no metric was selected, remove the source
			if (this.activeMetricsSource && !this.activeMetricsSource.settings.selectedMetric) {
				this.removeMetricsSource(this.activeMetricsSource);
			}
			this.showMetricsSourceModal = false;
			this.activeMetricsSource = null;
		},
		saveMetricsSourceSettings() {
			if (this.activeMetricsSource) { 
				const aM = general.stripObject ( this.activeMetricsSource );
				const index = this.componentSettings.selectedSources.findIndex(
					(source) => source.id === aM.id
				);
				let errors = [];
				function msg (msg) {
					errors.push(msg);
				}
				
				if (index > -1) {
					switch ( aM.type ) {

						case "rankTracking":
						case "dataDive":

							if ( !this.selectedProject ) {
								msg("Please select a " + this.labelProject.toLowerCase());
							}

							if ( !aM.settings.selectedMetric ) {
								msg("Please select a metric");
							}
							
							break;

						case "custom":

							if ( !aM.settings.metricLabel ) {
								msg("Please enter a metric label");
							}
							if ( !aM.settings.selectedMetric ) {
								msg("Please enter a value");
							}
							
							break;
							
					}
					if ( !errors.length ) {
						Vue.set(this.componentSettings.selectedSources, index, {
							...aM,
							settings: {
								...aM.settings
							}
						});
						this.closeMetricsSourceSettings();
					}
					else {
						this.$snotify.error(errors.join("\n"));
					}
				}
			}
			
		},
		getMetricsOptions(sourceType) {
			switch (sourceType) {
				case "dataDive":
					return this.dataDiveMetricsOptions;
				case "rankTracking":
					return this.rankTrackingMetricsOptions;
				default:
					return [];
			}
		},
		handleHeaderFooterClick(type) {
			// Find existing header/footer component
			const existingComponent = this.reportItems.find(item => item.type === type);
			
			if (existingComponent) {
				// If exists, show modal
				this.handleChange({ added: { element: existingComponent } });
			} else {
				// If doesn't exist, create and add it
				const newComponent = {
					id: `${type}_${Date.now()}`,
					name: type === 'header' ? 'Header' : 'Footer',
					type: type
				};
				
				// Add to start of list for header, end for footer
				if (type === 'header') {
					this.reportItems.unshift(newComponent);
				} else {
					this.reportItems.push(newComponent);
				}
				
				// Show settings modal
				this.handleChange({ added: { element: newComponent } });
			}
		},
		getFriendlyError(index) {
			let a = this.friendlyErrors.find(error => error.index === index);
			if ( a ) return ( a && a.error && typeof a.error === 'string' ) ? a.error : 'Error';
			return null;
		},
		loadReportData ( data ) {
			Vue.set(this, 'reportItems', data.config);
			this.reportCSV = data.csv;
			this.reportName = data.name;
			if ( data.settings ) {
				Object.keys( data.settings ).forEach(
					function(key) {
						this.reportSettings[key] = data.settings[key];
					}.bind(this)
				);
			}
			this.theReportId = data._id;
			this.projectIdIsDataDive = !!data.projectIdIsDataDive;
			this.projectId2 = data.projectId2 || null;
		}
	},

	async created() {
		this.updateLoadingText(this.reportId ? "Loading Report..." : true);
		this.componentFields ( 'all', 'componentSettings', {}, true );
		const ok = await this.getOptionsByProject(this.projectId);
		if (!ok) return;
		// Load saved searches
		try {
			const savedSearchesResponse = await ApiService.get("saved-searches/rank-tracking");
			this.savedSearches = savedSearchesResponse.data.map((search) => ({
				label: search.name,
				value: search._id,
			}));
		} catch (error) {
			console.error("Error loading saved searches:", error);
			this.updateLoadingText();
			return; // Stop execution if saved searches fail to load
		}

		// Load existing report if reportId exists
		if (this.reportId) {
			try {
				const report = await ApiService.get(`reports/${this.projectId}/${this.reportId}/get`);
				this.loadReportData ( report.data );
			} catch (error) {
				console.error("Error loading existing report:", error);
				this.$snotify.error("Failed to load report");
			}
		}
		if (this.$route.name === "report-builder-generate") {
			await this.generateReport();
		}
		this.updateLoadingText();
		this.buildQuillCSS ();
		this.fontCSS ();
	},
};
</script>

<style lang="less" scoped>
@import "../assets/css/less/_settings.less";
.component-icon {
	width: 1.5rem;
	height: 1.5rem;
	margin-right: 0.5rem;
}
.report-builder {
	display: flex;
	gap: 1rem;
	padding: 1rem;
	flex-wrap: wrap;
}
.override-title {
	margin-top: 2rem;
	padding-top: 2rem;
	border-top: 1px solid #ddd;
	margin-bottom: 1rem;
}

.report-components,
.report-canvas {
	flex: 1;
	border: 1px solid #ddd;
	border-radius: 4px;
	padding: 1rem;
	display: flex;
	flex-direction: column;
	min-height: 70vh;
	height: fit-content;
	position: relative;
	.report-title {
		background: @darkContrast;
		color: white;
		position: absolute;
		width: 100%;
		left: 0;
		top: 0;
		padding: 0.5rem 1rem;
		border-radius: 4px 4px 0 0;
	}
	.available-components-title {
		padding-bottom: 10px;
	}
}

.report-canvas {
	padding-top: 52px;
}

.dragArea {
	flex: 1;
	min-height: 100px;
	overflow-y: auto;
}

.no-components-message {
	text-align: center;
	padding: 1rem;
	color: #999;
	font-style: italic;
	margin: 0;
}

.component-item,
.dragged-item {
	padding: 0.5rem;
	margin: 0.5rem 0;
	background: #f5f5f5;
	border: 1px solid #ddd;
	border-radius: 4px;
	cursor: e-resize;
	display: flex;
}
.component-name {
	flex-grow:1;
}

.dragged-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	cursor: row-resize;
}

.remove-btn {
	background: none;
	border: none;
	color: @red;
	cursor: pointer;
	font-size: 1.2rem;

	&:hover {
		color: #cc0000;
	}
}

.modal {
}

.setting-group {
	margin: 1rem 0;

	&:first-child {
		margin-top: 0;
	}

	label {
		display: block;
		margin-bottom: 0.15rem;
		font-size: 14px;
		opacity: 0.8;
		font-weight: normal;
	}

	.settings-group-description {
		padding: 0;
		margin: 0 0 1rem;
	}

	.settings-group-title {
		margin-bottom: 0.5rem;
	}

	.large-button {
		&.mini {
			margin: 10px 20px 10px 0;
		}
	}

	.logo-preview {
		height: auto;
		max-width: 100px;
		max-height: 100px;
	}
	.settings-group-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
		gap: 1rem;
		margin-bottom: 1rem;
	}
	hr {
		margin: 2em 0;
	}
	.setting-group-input {
	}
}

// Add vue-select styles
:deep(.v-select) {
	background: white;
	border: 1px solid #ddd;
	border-radius: 4px;
}

.caddy-score-toggle {
	margin-top: 1rem;

	.caddy-score-label {
		margin-top: 1rem;
	}
}

.tag-select {
	margin-top: 1rem;
}

.keywords-input {
	margin-top: 1rem;
}

.settings-drag-drop {
	display: flex;
	gap: 1rem;
	margin-top: 1rem;
}

.settings-drag-drop-components,
.settings-drag-drop-canvas {
	flex: 1;
	border: 1px solid #ddd;
	border-radius: 4px;
	padding: 1rem;
	min-height: 200px;

	h4 {
		margin: 0;
	}
}

.dragArea {
	min-height: 150px;
}

.element-controls {
	display: flex;
	align-items: center;
	gap: 0.5rem;

	input {
		padding: 0.25rem;
		border: 1px solid #ddd;
		border-radius: 3px;
		font-size: 0.9rem;
	}
}

.logo-controls {
	display: flex;
	align-items: center;
	gap: 1rem;
	width: 200px;

	.logo-preview {
		max-width: 50px;
		height: auto;
	}
}

.date-controls,
.project-controls {
	margin-left: auto;
	min-width: 200px;

	:deep(.v-select) {
		font-size: 0.9rem;
	}

	.v-select {
		margin-bottom: 0;

		:deep(.vs__dropdown-toggle) {
			margin-bottom: 0;
		}
	}
}

.project-controls {
	min-width: 120px;
}

.dragged-item {
	// ... existing styles ...

	// Add this to ensure proper alignment of controls
	&:has(.date-controls) {
		padding-right: 1rem;
	}
	&:has(.project-controls) {
		padding-right: 1rem;
	}
}

.saved-search-select {
	margin-top: 1rem;
}

.warning-text {
	font-size: 0.85rem;
	color: #856404;
	background-color: #fff3cd;
	border: 1px solid #ffeeba;
	border-radius: 4px;
	padding: 0.5rem;
	margin: 0.5rem 0;
}

.table-headings-builder {
	margin-top: 1rem !important;

	.headings-container {
		display: flex;
		gap: 1rem;
		margin-top: 0.5rem;

		.available-headings,
		.selected-headings {
			flex: 1;
			border: 1px solid #ddd;
			border-radius: 4px;
			padding: 1rem;

			h5 {
				margin: 0;
			}

			.headings-list {
				min-height: 100px;
				overflow-y: auto;
				flex: 1;
				display: flex;
				flex-direction: column;

				.heading-item {
					padding: 0.5rem;
					margin: 0.5rem 0;
					background: #f5f5f5;
					border: 1px solid #ddd;
					border-radius: 4px;
					cursor: move;
					display: flex;
					justify-content: space-between;
					align-items: center;

					&:last-child {
						margin-bottom: 0;
					}
				}

				// Add empty state styling
				&:empty {
					position: relative;
					padding: 1rem;

					&::after {
						content: "Drop columns here";
						color: #999;
						font-style: italic;
						position: absolute;
						top: 50%;
						left: 50%;
						transform: translate(-50%, -50%);
					}
				}
			}
		}
	}
}

.custom-days-input {
	margin-top: 1rem;
}

.custom-date-range {
	margin-top: 1rem;
}

.date-range-section {

	h4 {
		margin-top: 0;
		margin-bottom: 1rem;
	}
}

.dragged-item {
	// ... existing styles ...

	// Add specific styling for line break items
	&[data-type="lineBreak"] {
		background-color: #f8f9fa;
		border-style: dashed;
	}
}

.report-actions {
	display: flex;
	gap: 1rem;
	width: 100%;
	box-sizing: border-box;
	justify-content: space-between;
	align-items: center;
	padding: 0.5rem 1rem;
	background-color: #fff;
	border-radius: 5px;

	.report-name-input {
		flex: 1;
	}

	.action-buttons {
		display: flex;
		gap: 1rem;

		.large-button {
			margin: 0;
		}
	}
}

.report-content-wrapper {
	display: flex;
	gap: 1rem;
	background: white;
	border-radius: 5px;
	padding: 1rem;
	width: 100%;
	box-sizing: border-box;
}

.text-controls {
	display: flex;
	align-items: center;
	gap: 1rem;

	.text-preview {
		color: #666;
		font-size: 0.9em;
		font-style: italic;
		max-width: 200px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.custom-text-editor {
	.modal-inner-scroll {
		.setting-group {
			margin-top: 0;
		}
	}

}

.tags-info {
	margin: 1rem 0 0;
	font-size: 0.85rem;
	color: #666;
	font-style: italic;
}

.modal-inner {
	display: flex;
	flex-direction: column;
	height: 100%;

	.modal-title {
		flex-shrink: 0; // Prevent title from shrinking
	}

	.modal-inner-scroll {
		flex: 1;
		overflow-y: auto;
		display: flex;
		flex-direction: column;

		// Add padding at the bottom to prevent content from being hidden behind buttons
		.form {
			display: flex;
			flex-direction: column;
			min-height: 100%;
			padding-bottom: 70px; // Height of buttons + margin

			.option-wrap {
				flex: 1;
			}
		}
	}
	
}

// Adjust custom text editor modal specifically
.custom-text-editor {
	.modal-inner-scroll {
		.setting-group {
			margin-top: 0;
			margin-bottom: 10px; // Add space for fixed buttons
		}
	}
}

.report-name-section {
	display: flex;
	align-items: flex-end;
	gap: 0.5rem;
	max-width: 440px;
	flex: 1;

	.icon {
		cursor: pointer;
		color: #666;
		margin-bottom: 8px;
		width: 20px;

		&:hover {
			color: #333;
		}
	}
}

.background-images-section {
	margin-top: 1rem;

	.background-images-list {
		margin: 1rem 0;
		min-height: 50px;
	}

	.background-image-item {
		display: flex;
		gap: 1rem;
		align-items: flex-start;
		padding: 1rem;
		background: #f5f5f5;
		border: 1px solid #ddd;
		border-radius: 4px;
		margin-bottom: 1rem;

		.drag-handle {
			cursor: row-resize;
			padding: 0;
			display: flex;
			align-items: center;
			color: #666;

			.drag-handle-icon {
				width: 24px;
				height: 24px;
				opacity: 0.5;
			}
			&:hover {
				.drag-handle-icon {
					opacity: 1;
				}
			}
		}

		.image-preview {
			width: 100px;

			img {
				width: 100%;
				height: auto;
				border-radius: 4px;
			}
		}

		.image-settings {
			flex: 1;

			.setting-row {
				display: flex;
				gap: 1rem;
				margin-bottom: 1rem;

				> * {
					flex: 1;
				}

				&:last-child {
					margin-bottom: 0;
				}
			}
		}

		.remove-btn {
			padding: 0.5rem;
			font-size: 1.2rem;
			line-height: 1;
			height: fit-content;
		}
	}
}

.size-setting {
	label {
		display: block;
		margin-bottom: 0.5rem;
		font-weight: 500;
	}
}


.report-settings-modal {
	.setting-group {
		// Add spacing between portrait/landscape toggle and colors
		&.landscape-toggle {
			margin-top: 0.5rem;
			margin-bottom: 1.5rem;
		}
	}
}

// Add font preview styles for dropdown options
:deep(.vs__dropdown-option) {
	&[data-font="Roboto"] {
		font-family: "Roboto", sans-serif;
	}
	&[data-font="Courier"] {
		font-family: "Courier", monospace;
	}
	&[data-font="Helvetica"] {
		font-family: "Helvetica", sans-serif;
	}
	&[data-font="Times"] {
		font-family: "Times", serif;
	}
}

// Add these new styles
.colors-row {
	display: flex;
	gap: 2rem;
	align-items: flex-start;

	.color-setting, .landscape-toggle {
		flex: 1;
	}
}

.logo-preview-mini {
	width: 30px;
	height: 30px;
	overflow: hidden;
	border-radius: 3px;
	margin-right: 10px;

	img {
		width: 100%;
		height: 100%;
		object-fit: contain;
	}
}

.element-settings-modal {
	.setting-group {
		margin-bottom: 1.5rem;

		&:last-child {
			margin-bottom: 0;
		}

		.logo-preview {
			margin-top: 0;
			max-width: 200px;
			max-height: 100px;
			height: auto;
		}
	}
	p {
		margin: 0;
	}
}

.table-defaults-section {
	.table-row-settings {
		margin-top: 20px;

		h5 {
			margin-top: 0;
			margin-bottom: 15px;
			font-size: 1em;
			color: #666;
		}

		.style-controls {
			display: grid;
			grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
			gap: 15px;
			align-items: start;

			.form-input {
				margin-bottom: 0;
			}
		}

		.alternate-row-toggle {
			margin-bottom: 15px;
		}
	}
}

.dragged-item {
	// ... existing styles ...

	.element-controls {
		display: flex;
		align-items: center;

		.logo-preview-mini {
			margin-right: 10px;
		}
	}
}

.margins-section {
	h4 {
		margin-bottom: 1rem;
	}

	.margins-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
		gap: 1rem;
	}

	.margin-help-text {
		margin-top: 0.75rem;
		font-size: 0.9em;
		color: #666;
		font-style: italic;
	}
}

.border-settings {
	margin-bottom: 1rem;

	h5 {
		margin: 0 0 0.5rem;
		font-size: 0.9em;
		color: #666;
	}

	.border-controls {
		display: flex;
		gap: 1rem;
		align-items: flex-start;
		margin-top: 1rem;

		> * {
			flex: 1;
		}
	}

	.color-setting {
		flex: 1;
	}

	.border-help-text {
		font-size: 0.85em;
		color: #856404;
		font-style: italic;
	}
}

.border-help-text {
	font-size: 0.85em;
	color: #856404;
	font-style: italic;
}

.table-defaults-section {
	margin-top: 2rem;
	padding-top: 2rem;
	border-top: 1px solid #ddd;

	h4 {
		margin-bottom: 1.5rem;
	}

	.border-settings {
		margin-bottom: 2rem;

		&:last-child {
			margin-bottom: 0;
		}

		h5 {
			margin: 0 0 1rem;
			font-size: 1em;
			color: #666;
		}

		.border-controls {
			display: flex;
			gap: 2rem;
			align-items: flex-start;

			> * {
				flex: 1;
			}
		}

		.border-help-text {
			margin-top: 0.5rem;
			font-size: 0.9em;
			color: #856404;
			font-style: italic;
		}
	}
}

.table-row-settings {
	margin-top: 1rem;

	h5 {
		margin-bottom: 0.5rem;
		font-size: 0.9em;
		color: #666;
	}

	.style-controls {
		display: flex;
		gap: 1rem;
		align-items: flex-start;

		> * {
			flex: 1;
		}
	}

	.color-setting {
		flex: 1;
	}
	
}

.alternate-row-toggle {
	margin-top: 0.5rem;
	margin-bottom: 0.5rem;
	font-size: 0.9em;
	color: #666;
}
.csv-help-text {
	font-size: 12px;
	color: #666;
	margin-bottom: 10px;
	padding: 0 10px 10px;
	background: #f5f5f5;
	border-radius: 4px;

	p {
		margin: 0;
		padding: 10px 0 0;
		a {
			color: inherit;
			text-decoration: underline;
		}
	}

	ul {
		margin: 5px 0 0 20px;
		padding: 0;

		li {
			margin-bottom: 3px;
		}
	}
}

.logo-dimensions {
	display: flex;
	justify-content: flex-start;
	gap: 15px;
	margin-bottom: 1rem;

	.logo-dimensions-input {

		min-width: 150px;
		flex: 1;

	}

	:deep(.form-input) {
		flex: 1;
		min-width: 0; // Prevents flex items from overflowing

		input {
			width: 100%;
		}
	}
}

.report-layout {
    display: flex;
    flex-direction: column;
    gap: 5px;
    
    .large-button {
        align-self: center;
        width: auto;
        min-width: 150px;
		margin-top: 0;
		&:first-child {
			
		}
    }

    // Restore original list styling
    .dragArea {
        min-height: 50px;
        background: #fff;
        border-radius: 4px;
        margin: 0;
        
        .list-group-item {
            cursor: pointer;
            padding: 10px;
            margin: 5px 0;
            background: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;

			&.has-remove {

				display: flex;
				justify-content: space-between;

			}
            
            &:hover {
                background: #e9ecef;
            }

			&.newPage {
				margin-top: 20px;
				&:first-child {
					margin-top: 5px;
				}
				span {
					span {
						font-weight: @bold;
					}
				}
			}

			&.indented {
				margin-left: 20px;
			}

			&.had-page-break {
				background: #f0f0f0;
			}

			&.header, &.footer {
				display: none;
			}

			&.error {
				background-color: @red;
				.remove-btn {
					color: #fff;
				}
				span {
					span {
						color: #fff;
						opacity: 0.8;
						&.error-message {
							opacity: 1;
						}
					}
				}
			}

        }
    }
}
.toggled {
	background-color: fade(@formDefaultColour, 5%);
	padding: 1rem;
	border-radius: 0.5rem;
	margin-top: 1rem;
}
.toggle-link {
	cursor: pointer;
	opacity: 0.9;
	font-weight: normal;
	display: inline-block;
	margin-left: 0.4rem;
	&:hover {
		opacity: 1;
	}
}
@media screen and (min-width: 768px) {
	.settings-group-2-768 {
		display: flex;
		gap: 20px;
		.setting-group {
			width: 50%;
			margin: 0;
			.v-select {
				margin: 0;
			}
		}
	}
}
</style>